import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  memo,
  useCallback,
  useMemo
} from 'react'
import { useRouter, useParams, usePathname } from 'next/navigation'
import { safeConsole } from '@/lib/utils/safeConsole'
import { ChatbotUIChatContext, ScopedChatBindings } from '@/context/chat'
import { ChatHandlerContext, type StructuredMentionConfig } from '@/context/chat-handler'
import { ChatbotUIContext, ScopedAttachmentBindings } from '@/context/context'
import { useInput } from '@/contexts/input-context'
import { ChatMessage } from '@/types'
import { ChatSendOptions, ChatStopOptions } from '@/types/chat'
import { UserExtension } from '@/types/extensions'
import type { GroupChatMemberSummary } from '@/types/chat-groups'
import type { WorkspaceMember } from '@/types/workspace-invitations'
import { Tables } from '@/supabase/types'
import {
  IconArrowUp,
  IconBulb,
  IconCamera,
  IconCheck,
  IconMicrophone,
  IconPaperclip,
  IconPlayerStopFilled,
  IconPlus,
  IconPrompt,
  IconRobotFace,
  IconWaveSine,
  IconX
} from '@tabler/icons-react'
import { AnimatePresence, motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { VscRobot } from 'react-icons/vsc'
import { toast } from 'sonner'

import useHotkey from '@/lib/hooks/use-hotkey'
import { useChatTypingPresence } from '@/lib/hooks/useChatTypingPresence'
import {
  createAudioInputConstraints,
  getBrowserMicrophonePermissionState,
  getAudioInputDeviceIdFromStream,
  getUserMediaWithNativePermissions,
  isAudioInputDeviceUnavailableError,
  isMediaRecorderCaptureSupported,
  listAudioInputDevices,
  persistPreferredAudioInputDeviceId,
  readPreferredAudioInputDeviceId,
  type AudioInputDeviceOption
} from '@/lib/desktop/native-media'
import { LLM_LIST } from '@/lib/models/llm/llm-list'
import { cn } from '@/lib/utils'
import { SpeechInput } from '@/components/ai-elements/speech-input'
import { AssistantIcon } from '@/components/assistants/assistant-icon'
import { ChatSelectedHtmlElements } from '@/components/chat/chat-selected-html-elements'
import { ChatTypingIndicator } from '@/components/chat/chat-typing-indicator'

import { Input } from '../ui/input'
import { TextareaAutosize } from '../ui/textarea-autosize'
import { AdvancedVoiceMode } from './advanced-voice-mode'
import { RealtimeComposerBar } from './realtime/realtime-composer-bar'
import { ChatCommandInput } from './chat-command-input'
import { ChatFilesDisplay } from './chat-files-display'
import { useChatHandler } from './chat-hooks/use-chat-handler'
import { useChatHistoryHandler } from './chat-hooks/use-chat-history'
import { usePromptAndCommand } from './chat-hooks/use-prompt-and-command'
import { useScopedAttachmentKey, useScopedUserInput } from './chat-hooks/use-scoped-user-input'
import {
  useSelectFileHandler,
  type ChatAttachmentUploadStatus
} from './chat-hooks/use-select-file-handler'
import { ImageGenToggle } from './image-gen-toggle'
import { WebBrowseToggle } from './web-browse-toggle'
import { ThinkSelector } from './think-selector'
import { PublicReasoningToggle } from './public-reasoning-toggle'
import { QuotedText } from './quoted-text'
import { QueuedMessagesBlock } from './queued-message'
import { SelectedText } from './selected-text'
import { SpaceItemContextChip } from './space-item-context-chip'
import { Button } from '@/components/ui/button'
import { FloatingSurface } from '@/components/ui/floating-surface'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { VideoGenToggle } from './video-gen-toggle'
import { MusicGenToggle } from './music-gen-toggle'
import { ToolDropdown } from './tool-dropdown'
import { AttachmentDropdown } from './attachment-dropdown'
import { useSelectionInput } from './chat-hooks/use-selection-input'
import { noteActions } from '@/lib/note-actions'
import { REGEX_NOTE } from '@/lib/preview'
import { IconSparkle2 } from '@tabler/icons-react'
import { detectExtensionInMessage } from '@/lib/utils/extension-detection'
import { useAuth } from '@/context/auth'
import { CrmMentionsInput } from '@/components/chat/crm-mentions-input'
import {
  EVERYONE_MENTION_OPTION,
  LOSI_PLATFORM_MENTION_OPTION,
  LOSI_SUPER_AGENT_MENTION_OPTIONS,
  filterUnifiedMentionOptions,
  getFirstUnifiedMentionOption,
  replaceActiveMention,
  toAssistantMentionOption,
  type UnifiedHumanMentionOption,
  type UnifiedMentionOption
} from '@/components/chat/unified-mention-picker'
import {
  getGroupChatResponseMode,
  shouldEnableChatInputAssistantAutocomplete
} from '@/lib/chat/group-chat-response-mode'
import {
  clearWorkspaceBootstrapDraft,
  readWorkspaceBootstrapDraft
} from '@/lib/chat/workspace-bootstrap-draft'
import { getGroupMembers } from '@/lib/services/chat-group-service'
import { workspaceInvitationService } from '@/lib/services/workspace-invitation-service'
import {
  dispatchSidekickOpenSpaceItem,
  getSidekickSpaceItemHref,
  serializeSidekickSpaceItemTag
} from '@/lib/sidekick/space-item-context'
import {
  parseChatProductCreationCommand,
  resolveSiteExperienceIdFromPrompt,
  type ChatProductCreationCommand
} from '@/lib/chat/product-creation'
import { shouldSuppressEngineProductCreationForContentEngine } from '@/lib/chat/content-engine-routing'
import { shouldPreserveMessageTextSelection } from '@/lib/chat/message-text-selection'
import { shouldPreserveComputerInteractionFocus } from '@/lib/chat/computer-interaction-focus'
import {
  formatVoiceMemoTranscriptForModel,
  normalizeVoiceMemoWaveformSamples,
  updateChatVoiceMemoTranscript
} from '@/lib/chat/voice-memo'
import { rememberVoiceNoteCorrection } from '@/lib/chat/voice-note-dictionary'
import { getVoiceInputMimeType, transcribeRecordedVoiceMemo } from '@/lib/chat/voice-memo-client'
import type { ChatVoiceMemoMetadata } from '@/types/voice-memo'
import { VoiceMemoComposerPreview } from './voice-memo-composer-preview'
import { useFastDictation } from '@/hooks/useFastDictation'
import { useOptionalDiscoverability } from '@/lib/discoverability/context'

type HumanMentionInput = {
  id: string
  label: string
  imageUrl: string | null
  description: string
  source: UnifiedHumanMentionOption['source']
}

type WorkspaceMentionMemberResponse = {
  userId: string
  role: string
  displayName: string | null
  username: string | null
  email: string | null
  imageUrl: string | null
}

type VoiceMemoSendOverride = {
  transcript: string
  userInput: string
  voiceMemo: ChatVoiceMemoMetadata
  voiceRecordingDurationSeconds: number | null
  voiceWaveformSamples: number[]
}

const VOICE_WAVEFORM_SAMPLE_COUNT = 48
const VOICE_RECORDER_CHUNK_INTERVAL_MS = 250
const VOICE_RECORDER_FINAL_FLUSH_DELAY_MS = 80
const VOICE_WAVEFORM_SAMPLE_INTERVAL_MS = 33
const VOICE_WAVEFORM_SMOOTHING = 0.55
const VOICE_SILENCE_WARNING_DELAY_MS = 1600
// Holding the mic at least this long turns release into stop + transcribe +
// send. A quicker tap latches recording on hands-free instead.
const VOICE_HOLD_TO_SEND_MIN_MS = 350
const VOICE_SILENCE_SAMPLE_MINIMUM = 18
const VOICE_SILENCE_PEAK_THRESHOLD = 0.018
const COMPOSER_DRAFT_SCROLL_CLASS_NAME =
  'min-w-0 max-w-full max-h-[min(32dvh,12rem)] overflow-x-hidden overscroll-contain break-words [scrollbar-gutter:stable] !overflow-y-auto'
const STRUCTURED_COMPOSER_DRAFT_SCROLL_CLASS_NAME = cn(
  COMPOSER_DRAFT_SCROLL_CLASS_NAME,
  '[&_.crm-mentions-control]:max-h-[min(32dvh,12rem)]',
  '[&_.crm-mentions-control]:!overflow-y-auto',
  '[&_.crm-mentions-highlighter]:max-h-[min(32dvh,12rem)]',
  '[&_.crm-mentions-highlighter]:!overflow-y-auto',
  '[&_.crm-mentions-input-el]:max-h-[min(32dvh,12rem)]',
  '[&_.crm-mentions-input-el]:!overflow-y-auto'
)

function formatUploadFileSize(bytes: number | null | undefined) {
  if (!bytes || bytes <= 0) {
    return ''
  }

  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  const precision = value >= 10 || unitIndex === 0 ? 0 : 1
  return `${value.toFixed(precision)} ${units[unitIndex]}`
}

function getUploadProgressPercent(status: ChatAttachmentUploadStatus | null, isUploading: boolean) {
  if (status) {
    return Math.round(status.progress)
  }

  return isUploading ? 8 : 0
}

type AudioContextConstructor = new () => AudioContext

function readWaveformSignal(data: Uint8Array) {
  if (data.length === 0) {
    return {
      signalLevel: 0,
      visualLevel: 0.1
    }
  }

  let sum = 0
  let peak = 0
  data.forEach((value) => {
    const centered = Math.abs((value - 128) / 128)
    sum += centered * centered
    peak = Math.max(peak, centered)
  })

  const rms = Math.sqrt(sum / data.length)
  return {
    signalLevel: Math.max(rms, peak),
    visualLevel: Math.min(1, Math.max(0.1, rms * 10, peak * 2.5))
  }
}

function getLocalVoiceMemoObjectUrl(voiceMemo: ChatVoiceMemoMetadata | null) {
  if (!voiceMemo || voiceMemo.audioPath !== null) {
    return null
  }

  return voiceMemo.audioUrl.startsWith('blob:') ? voiceMemo.audioUrl : null
}

function getVoiceInputDeviceLabel(device: AudioInputDeviceOption | null | undefined) {
  return device?.label?.trim() || 'this microphone'
}

function canRecordVoiceInput() {
  return isMediaRecorderCaptureSupported()
}

/** Stable noop so ChatInput effect deps never see a fresh `() => {}` each render. */
const noopFocusChatInput = () => {}

function getGroupMemberMentionBaseName(member: GroupChatMemberSummary) {
  if (member.displayName?.trim()) {
    return member.displayName.trim()
  }

  if (member.email?.trim()) {
    return member.email.trim().split('@')[0] || member.email.trim()
  }

  return `member${member.userId.slice(0, 6)}`
}

function getWorkspaceMemberMentionBaseName(member: WorkspaceMember) {
  const displayName = member.user?.displayName?.trim()
  if (displayName) {
    return displayName
  }

  const username = member.user?.username?.trim()
  if (username) {
    return username
  }

  const email = member.user?.email?.trim()
  if (email) {
    return email.split('@')[0] || email
  }

  return `member${member.userId.slice(0, 6)}`
}

function toMentionToken(value: string) {
  return value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 32)
}

function createHumanMentionOptions(members: HumanMentionInput[]): UnifiedHumanMentionOption[] {
  const usedTokens = new Set<string>()

  return members.map((member) => {
    const baseToken = toMentionToken(member.label) || `member${member.id.slice(0, 6)}`
    let token = baseToken
    let suffixLength = 4

    while (usedTokens.has(token.toLowerCase())) {
      token = `${baseToken}${member.id.slice(0, suffixLength)}`
      suffixLength += 2
    }

    usedTokens.add(token.toLowerCase())

    return {
      kind: 'human',
      id: member.id,
      token,
      label: member.label,
      description: member.description,
      imageUrl: member.imageUrl,
      source: member.source
    }
  })
}

function createGroupMemberMentionOptions(
  members: GroupChatMemberSummary[],
  currentUserId?: string | null
): UnifiedHumanMentionOption[] {
  return createHumanMentionOptions(
    members
      .filter((member) => member.userId !== currentUserId)
      .map((member) => ({
        id: member.userId,
        label: getGroupMemberMentionBaseName(member),
        imageUrl: member.imageUrl,
        description: member.email || member.role || 'Group member',
        source: 'group'
      }))
  )
}

function createWorkspaceMemberMentionOptions(
  members: WorkspaceMember[],
  currentUserId?: string | null
): UnifiedHumanMentionOption[] {
  return createHumanMentionOptions(
    members
      .filter((member) => member.userId !== currentUserId)
      .map((member) => ({
        id: member.userId,
        label: getWorkspaceMemberMentionBaseName(member),
        imageUrl: member.user?.avatarUrl ?? null,
        description: member.user?.email || member.role,
        source: 'workspace'
      }))
  )
}

function createWorkspaceMentionMemberOptions(
  members: WorkspaceMentionMemberResponse[],
  currentUserId?: string | null
): UnifiedHumanMentionOption[] {
  return createHumanMentionOptions(
    members
      .filter((member) => member.userId !== currentUserId)
      .map((member) => ({
        id: member.userId,
        label:
          member.displayName ||
          member.username ||
          member.email?.split('@')[0] ||
          `member${member.userId.slice(0, 6)}`,
        imageUrl: member.imageUrl,
        description: member.email || member.role,
        source: 'workspace'
      }))
  )
}

function isWorkspaceMentionMemberResponseList(
  members: WorkspaceMember[] | WorkspaceMentionMemberResponse[]
): members is WorkspaceMentionMemberResponse[] {
  return members.length > 0 && 'displayName' in members[0]
}

const GROUP_MEMBER_MENTION_CACHE_TTL_MS = 5 * 60 * 1000
const groupMemberMentionCache = new Map<
  string,
  {
    expiresAt: number
    options: UnifiedHumanMentionOption[]
  }
>()
const groupMemberMentionRequests = new Map<string, Promise<UnifiedHumanMentionOption[]>>()

function getGroupMemberMentionCacheKey(chatId: string, currentUserId?: string | null) {
  return `${chatId}:${currentUserId ?? ''}`
}

function readCachedGroupMemberMentions(cacheKey: string) {
  const cached = groupMemberMentionCache.get(cacheKey)

  if (!cached || cached.expiresAt <= Date.now()) {
    groupMemberMentionCache.delete(cacheKey)
    return null
  }

  return cached.options
}

async function loadGroupMemberMentions(chatId: string, currentUserId?: string | null) {
  const cacheKey = getGroupMemberMentionCacheKey(chatId, currentUserId)
  const cachedOptions = readCachedGroupMemberMentions(cacheKey)

  if (cachedOptions) {
    return cachedOptions
  }

  const inFlightRequest = groupMemberMentionRequests.get(cacheKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const request = getGroupMembers(chatId)
    .then((snapshot) => {
      const options = createGroupMemberMentionOptions(snapshot.members, currentUserId)

      groupMemberMentionCache.set(cacheKey, {
        expiresAt: Date.now() + GROUP_MEMBER_MENTION_CACHE_TTL_MS,
        options
      })

      return options
    })
    .finally(() => {
      groupMemberMentionRequests.delete(cacheKey)
    })

  groupMemberMentionRequests.set(cacheKey, request)

  return request
}

const WORKSPACE_MEMBER_MENTION_CACHE_TTL_MS = 5 * 60 * 1000
const workspaceMemberMentionCache = new Map<
  string,
  {
    expiresAt: number
    options: UnifiedHumanMentionOption[]
  }
>()
const workspaceMemberMentionRequests = new Map<string, Promise<UnifiedHumanMentionOption[]>>()

function getWorkspaceMemberMentionCacheKey(workspaceId: string, currentUserId?: string | null) {
  return `${workspaceId}:${currentUserId ?? ''}`
}

function readCachedWorkspaceMemberMentions(cacheKey: string) {
  const cached = workspaceMemberMentionCache.get(cacheKey)

  if (!cached || cached.expiresAt <= Date.now()) {
    workspaceMemberMentionCache.delete(cacheKey)
    return null
  }

  return cached.options
}

async function loadWorkspaceMemberMentions(workspaceId: string, currentUserId?: string | null) {
  const cacheKey = getWorkspaceMemberMentionCacheKey(workspaceId, currentUserId)
  const cachedOptions = readCachedWorkspaceMemberMentions(cacheKey)

  if (cachedOptions) {
    return cachedOptions
  }

  const inFlightRequest = workspaceMemberMentionRequests.get(cacheKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const request = fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/mention-members`, {
    cache: 'no-store',
    credentials: 'include'
  })
    .then(async (response) => {
      if (!response.ok) {
        return workspaceInvitationService.getWorkspaceMembers(workspaceId)
      }

      const payload = (await response.json().catch(() => null)) as {
        members?: WorkspaceMentionMemberResponse[]
      } | null

      if (!Array.isArray(payload?.members)) {
        return workspaceInvitationService.getWorkspaceMembers(workspaceId)
      }

      return payload.members
    })
    .then((members) => {
      const options = isWorkspaceMentionMemberResponseList(members)
        ? createWorkspaceMentionMemberOptions(members, currentUserId)
        : createWorkspaceMemberMentionOptions(members, currentUserId)

      workspaceMemberMentionCache.set(cacheKey, {
        expiresAt: Date.now() + WORKSPACE_MEMBER_MENTION_CACHE_TTL_MS,
        options
      })

      return options
    })
    .finally(() => {
      workspaceMemberMentionRequests.delete(cacheKey)
    })

  workspaceMemberMentionRequests.set(cacheKey, request)

  return request
}

interface ChatInputProps {
  chatId?: string
  fileWorkspaceId?: string | null
  showAssistant: boolean
  handleSendMessage?: (
    message: string,
    chatMessages: ChatMessage[],
    isUserMessage: boolean,
    options?: ChatSendOptions
  ) => void | Promise<void>
  handleStopMessage?: (options?: ChatStopOptions) => void
  isPublic?: boolean
  realtimeEnabled?: boolean
  showWebBrowseToggle?: boolean
  showReasoningButton?: boolean
  /** Shared layout id used by the primary chat surface composer transition. */
  composerLayoutId?: string
  /** Optional placeholder rotation override for owner surfaces. */
  placeholderTexts?: string[]
  /** Plan mode: when user triggers Plan (e.g. Shift+Tab or Plan button), call with current message content */
  onPlanModeClick?: (messageContent: string) => void
  /** Whether a plan already exists for this chat */
  hasPlan?: boolean
  /** When true, the current assistant turn is an active Sidecar run */
  sidecarRunActive?: boolean
  /** Number of follow-up user turns queued behind the current Sidecar run */
  sidecarQueueCount?: number
  /** Treat a chat surface without a persisted chat id as an isolated draft. */
  forceDraftChat?: boolean
  /** Whether this composer may request focus. */
  canFocusComposer?: boolean
  /** Queued follow-up messages (shown above input like quotes) */
  queuedSidecarTurns?: { id: string; message: string }[]
  /** Remove a message from the queue */
  onRemoveQueuedTurn?: (id: string) => void
  /** Update a queued message's text */
  onUpdateQueuedTurn?: (id: string, message: string) => void
  /** Send a queued message now (dequeue and send) */
  onSendQueuedTurnNow?: (id: string) => void
  /** Hidden beta product creation command handler. */
  onProductCreationCommand?: (command: ChatProductCreationCommand) => void
  /** Chat id whose workspace plan may authorize group-chat upload limits. */
  workspaceUploadLimitChatId?: string | null
  /** Space id whose workspace plan may authorize Space-chat upload limits. */
  workspaceUploadLimitSpaceId?: string | null
}

interface QuotedTextData {
  text: string
  messageId?: string
}

function resolveTypingDisplayName(
  profile: {
    display_name?: string | null
    username?: string | null
  } | null,
  email: string | null | undefined
) {
  const displayName = profile?.display_name?.trim()
  if (displayName) {
    return displayName
  }

  const username = profile?.username?.trim()
  if (username) {
    return username
  }

  const normalizedEmail = email?.trim()
  if (normalizedEmail) {
    return normalizedEmail
  }

  return 'Someone'
}

function getWorkspaceUploadLimitChatId(chat: Tables<'chats'> | null) {
  if (!chat) {
    return null
  }

  return chat.is_group_chat || chat.space_id || chat.type === 'space_conversation'
    ? chat.id
    : null
}

// Detect whether any overlay, panel, popover, search, or dialog is
// currently open in the DOM. Used to suppress autofocus on the chat
// input so it doesn't steal focus from these surfaces.
function hasActiveOverlay(): boolean {
  return Boolean(
    document.querySelector(
      [
        '[role="dialog"]',
        '[role="alertdialog"]',
        '[role="listbox"]',
        '[role="combobox"][data-state="open"]',
        '[data-radix-popper-content-wrapper]',
        '[data-radix-focus-guard]',
        '[data-state="open"][role="menu"]',
        '[data-state="open"][data-radix-menu-content]',
        '[data-state="open"][data-radix-popover-content]',
        '[data-state="open"][data-radix-dropdown-menu-content]',
        '[cmdk-dialog]',
        '[data-sonner-toaster][data-expanded="true"]'
      ].join(', ')
    )
  )
}

// Performance-optimized comparison function
const areInputPropsEqual = (prevProps: ChatInputProps, nextProps: ChatInputProps) => {
  if (prevProps.chatId !== nextProps.chatId) return false
  if (prevProps.showAssistant !== nextProps.showAssistant) return false
  if (prevProps.handleSendMessage !== nextProps.handleSendMessage) return false
  if (prevProps.handleStopMessage !== nextProps.handleStopMessage) return false
  if (prevProps.isPublic !== nextProps.isPublic) return false
  if (prevProps.realtimeEnabled !== nextProps.realtimeEnabled) return false
  if (prevProps.showWebBrowseToggle !== nextProps.showWebBrowseToggle) return false
  if (prevProps.showReasoningButton !== nextProps.showReasoningButton) return false
  if (prevProps.composerLayoutId !== nextProps.composerLayoutId) return false
  if (prevProps.placeholderTexts !== nextProps.placeholderTexts) return false
  if (prevProps.onPlanModeClick !== nextProps.onPlanModeClick) return false
  if (prevProps.hasPlan !== nextProps.hasPlan) return false
  if (prevProps.sidecarRunActive !== nextProps.sidecarRunActive) return false
  if (prevProps.sidecarQueueCount !== nextProps.sidecarQueueCount) return false
  if (prevProps.forceDraftChat !== nextProps.forceDraftChat) return false
  if (prevProps.canFocusComposer !== nextProps.canFocusComposer) return false
  const prevQueuedTurns = prevProps.queuedSidecarTurns ?? []
  const nextQueuedTurns = nextProps.queuedSidecarTurns ?? []
  if (prevQueuedTurns.length !== nextQueuedTurns.length) return false
  for (let index = 0; index < prevQueuedTurns.length; index += 1) {
    const prevTurn = prevQueuedTurns[index]
    const nextTurn = nextQueuedTurns[index]

    if (
      !prevTurn ||
      !nextTurn ||
      prevTurn.id !== nextTurn.id ||
      prevTurn.message !== nextTurn.message
    ) {
      return false
    }
  }
  if (prevProps.onRemoveQueuedTurn !== nextProps.onRemoveQueuedTurn) return false
  if (prevProps.onUpdateQueuedTurn !== nextProps.onUpdateQueuedTurn) return false
  if (prevProps.onSendQueuedTurnNow !== nextProps.onSendQueuedTurnNow) return false
  if (prevProps.onProductCreationCommand !== nextProps.onProductCreationCommand) return false
  if (prevProps.fileWorkspaceId !== nextProps.fileWorkspaceId) return false
  if (prevProps.workspaceUploadLimitChatId !== nextProps.workspaceUploadLimitChatId) return false
  if (prevProps.workspaceUploadLimitSpaceId !== nextProps.workspaceUploadLimitSpaceId) return false
  return true
}

// Memoize the input component to prevent unnecessary re-renders
const ChatInputInternal = memo(
  ({
    chatId: propChatId,
    fileWorkspaceId: propFileWorkspaceId = null,
    showAssistant = true,
    handleSendMessage,
    handleStopMessage: propHandleStopMessage,
    isPublic = false,
    realtimeEnabled = true,
    showWebBrowseToggle = false,
    showReasoningButton = false,
    composerLayoutId,
    placeholderTexts,
    onPlanModeClick,
    hasPlan = false,
    sidecarRunActive = false,
    forceDraftChat = false,
    canFocusComposer = true,
    queuedSidecarTurns = [],
    onRemoveQueuedTurn,
    onUpdateQueuedTurn,
    onSendQueuedTurnNow,
    onProductCreationCommand,
    workspaceUploadLimitChatId: propWorkspaceUploadLimitChatId = null,
    workspaceUploadLimitSpaceId: propWorkspaceUploadLimitSpaceId = null
  }: ChatInputProps) => {
    const { t } = useTranslation()
    const discoverability = useOptionalDiscoverability()

    useHotkey('l', () => {
      if (!canFocusComposer) return
      if (shouldPreserveComputerInteractionFocus()) return
      if (hasActiveOverlay()) return
      handleFocusChatInput()
    })

    // Memoize state to prevent unnecessary re-renders
    const [isTyping, setIsTyping] = useState<boolean>(false)
    const [userInputBeforeRecording, setUserInputBeforeRecording] = useState('')
    const [transcript, setTranscript] = useState<string>('')
    const [voiceMemo, setVoiceMemo] = useState<ChatVoiceMemoMetadata | null>(null)
    const [listening, setListening] = useState(false)
    const [voiceInputSupported, setVoiceInputSupported] = useState(false)
    const [isTranscribingVoice, setIsTranscribingVoice] = useState(false)
    const [voiceWaveformSamples, setVoiceWaveformSamples] = useState<number[]>([])
    const [voiceRecordingStartedAt, setVoiceRecordingStartedAt] = useState<number | null>(null)
    const [voiceRecordingDurationSeconds, setVoiceRecordingDurationSeconds] = useState<
      number | null
    >(null)
    const [voiceInputDevices, setVoiceInputDevices] = useState<AudioInputDeviceOption[]>([])
    const [selectedVoiceInputDeviceId, setSelectedVoiceInputDeviceId] = useState<string | null>(
      null
    )
    const [pendingVoiceInputDeviceId, setPendingVoiceInputDeviceId] = useState<string | null>(null)
    const [voiceDevicePopoverOpen, setVoiceDevicePopoverOpen] = useState(false)
    const [silentVoiceInputDeviceId, setSilentVoiceInputDeviceId] = useState<string | null>(null)
    const [autocompleteMention, setAutocompleteMention] = useState<UnifiedMentionOption | null>(
      null
    )
    const [autocompleteSuggestion, setAutocompleteSuggestion] = useState('')
    const [selectedAssistantAnimation, setSelectedAssistantAnimation] =
      useState<Tables<'assistants'> | null>(null)
    const [groupMemberMentionOptions, setGroupMemberMentionOptions] = useState<
      UnifiedHumanMentionOption[]
    >([])
    const [workspaceMemberMentionOptions, setWorkspaceMemberMentionOptions] = useState<
      UnifiedHumanMentionOption[]
    >([])
    const [groupMemberMentionTokensReady, setGroupMemberMentionTokensReady] = useState(false)
    const groupMemberMentionTokens = useMemo(
      () => groupMemberMentionOptions.map((member) => member.token.toLowerCase()),
      [groupMemberMentionOptions]
    )
    const [windowWidth, setWindowWidth] = useState<number>(
      typeof window !== 'undefined' ? window.innerWidth : 1920
    )
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const initialFocusAppliedRef = useRef(false)
    const voiceRecorderRef = useRef<MediaRecorder | null>(null)
    // Fast on-device dictation (Web Speech): instant live interim text while
    // recording, no server round trip, no realtime agent. The MediaRecorder
    // blob still uploads for the voice memo; its transcript prefers this.
    const liveDictation = useFastDictation()
    const liveDictationActiveRef = useRef(false)
    const stopLiveDictation = useCallback((): string => {
      if (!liveDictationActiveRef.current) return ''
      liveDictationActiveRef.current = false
      try {
        return liveDictation.stop().trim()
      } catch {
        return ''
      }
    }, [liveDictation])
    const voiceStreamRef = useRef<MediaStream | null>(null)
    const voiceChunksRef = useRef<Blob[]>([])
    const voiceWaveformSamplesRef = useRef<number[]>([])
    const voiceRecordingStartedAtRef = useRef<number | null>(null)
    const voiceAudioContextRef = useRef<AudioContext | null>(null)
    const voiceAnalyserFrameRef = useRef<number | null>(null)
    const voiceObjectUrlRef = useRef<string | null>(null)
    const voiceCancelRecordingRef = useRef(false)
    const voiceStopRequestedRef = useRef(false)
    const voiceSignalPeakRef = useRef(0)
    const voiceSignalSampleCountRef = useRef(0)
    const voiceSilenceWarningTimerRef = useRef<number | null>(null)
    const selectedVoiceInputDeviceIdRef = useRef<string | null>(null)
    const activeVoiceInputDeviceIdRef = useRef<string | null>(null)
    const voiceSendAfterStopRequestedRef = useRef(false)
    const voiceOriginalTranscriptRef = useRef<string | null>(null)
    // Hold-to-record gesture state. The mic button unmounts as soon as
    // recording starts, so the release is tracked on window listeners whose
    // detach is kept here for unmount cleanup.
    const voiceStartInFlightRef = useRef(false)
    const voiceHoldGestureRef = useRef<{ pointerId: number; startedAt: number } | null>(null)
    const voiceHoldClickGuardAtRef = useRef(0)
    const voiceHoldReleaseCleanupRef = useRef<(() => void) | null>(null)

    // Memoize context values to prevent unnecessary re-renders
    // Handle case where contexts might not be available (e.g., on homepage)
    const chatbotUIContext = useContext(ChatbotUIContext)
    const chatbotUIChatContext = useContext(ChatbotUIChatContext)
    const handlerOverride = useContext(ChatHandlerContext)

    const {
      isAssistantPickerOpen = false,
      setIsAssistantPickerOpen = () => {},
      setAtCommand = () => {},
      focusAssistant = false,
      setFocusAssistant = () => {},
      selectedPreset = null,
      selectedAssistant = null,
      setSelectedAssistant = () => {},
      focusPrompt = false,
      setFocusPrompt = () => {},
      focusFile = false,
      focusTool = false,
      setFocusTool = () => {},
      isToolPickerOpen = false,
      isPromptPickerOpen = false,
      setIsFilePickerOpen = () => {},
      setIsPromptPickerOpen = () => {},
      isFilePickerOpen = false,
      setFocusFile = () => {},
      profile = null,
      selectedWorkspace = null,
      assistants = [],
      setProfile = () => {},
      tools = [],
      selectedTools = [],
      setSelectedTools = () => {},
      allModels = [],
      newMessageFiles = [],
      newMessageImages = [],
      chatFiles = [],
      getAttachmentStoreKey = () => 'chat:draft',
      getScopedAttachmentBindings = () =>
        ({
          getSnapshot: () => ({
            chatFiles: [],
            chatImages: [],
            newMessageFiles: [],
            newMessageImages: [],
            showFilesDisplay: false
          }),
          setChatFiles: () => {},
          setChatImages: () => {},
          setNewMessageFiles: () => {},
          setNewMessageImages: () => {},
          setShowFilesDisplay: () => {}
        }) as ScopedAttachmentBindings
    } = chatbotUIContext || {}

    const {
      chatMessages = [],
      chatFileItems = [],
      isGenerating = false,
      isPersisting = false,
      chatSettings = null,
      selectedChat = null,
      getChatStoreKey = () => '',
      getScopedChatBindings = () =>
        ({
          getSnapshot: () => ({
            chatMessages: [],
            chatFileItems: [],
            abortController: null,
            firstTokenReceived: false,
            isGenerating: false,
            isPersisting: false,
            requestTokensTotal: 0,
            responseTimeToFirstToken: 0,
            responseTimeTotal: 0,
            responseTokensTotal: 0
          }),
          setChatMessages: () => {},
          setChatFileItems: () => {},
          setAbortController: () => {},
          setFirstTokenReceived: () => {},
          setIsGenerating: () => {},
          setIsPersisting: () => {},
          setRequestTokensTotal: () => {},
          setResponseTimeToFirstToken: () => {},
          setResponseTimeTotal: () => {},
          setResponseTokensTotal: () => {}
        }) as ScopedChatBindings
    } = chatbotUIChatContext || {}
    const router = useRouter()
    const pathname = usePathname()
    const { isSplitMode, navigateToHref } = useSplitPaneNavigation()
    const handleSplitNavigateToHref = useCallback(
      (href: string) => {
        if (isSplitMode) {
          navigateToHref(href)
          return
        }

        router.push(href)
      },
      [isSplitMode, navigateToHref, router]
    )
    const params = useParams()
    const routeChatId = params?.chatid as string | undefined
    const chatId =
      propChatId ??
      routeChatId ??
      (pathname === '/chat' ? undefined : (selectedChat?.id ?? undefined))
    const selectedChatForInput =
      chatId && selectedChat?.id === chatId
        ? selectedChat
        : !chatId && pathname !== '/chat'
          ? selectedChat
          : null
    const scopedChatState =
      chatbotUIChatContext && chatId
        ? getScopedChatBindings(getChatStoreKey(chatId)).getSnapshot()
        : null
    const scopedAttachmentKey = useScopedAttachmentKey({
      activeAttachmentKey:
        typeof chatbotUIContext?.activeAttachmentKey === 'string'
          ? chatbotUIContext.activeAttachmentKey
          : getAttachmentStoreKey(null),
      chatId,
      getAttachmentStoreKey
    })
    const scopedAttachmentState = chatbotUIContext
      ? getScopedAttachmentBindings(scopedAttachmentKey).getSnapshot()
      : null
    const effectiveChatMessages = scopedChatState?.chatMessages ?? chatMessages
    const effectiveChatFileItems = scopedChatState?.chatFileItems ?? chatFileItems
    const effectiveIsGenerating = scopedChatState?.isGenerating ?? isGenerating
    const effectiveIsPersisting = scopedChatState?.isPersisting ?? isPersisting
    const isComposerBusy =
      effectiveIsGenerating || effectiveIsPersisting || sidecarRunActive
    const effectiveNewMessageFiles = scopedAttachmentState?.newMessageFiles ?? newMessageFiles
    const effectiveNewMessageImages = scopedAttachmentState?.newMessageImages ?? newMessageImages
    const effectiveChatFiles = scopedAttachmentState?.chatFiles ?? chatFiles

    // Always call hooks unconditionally - React rules
    const authResult = useAuth()
    const user = authResult?.user || null
    const hasResolvedAuthSession = authResult?.session !== undefined
    const typingDisplayName = useMemo(
      () => resolveTypingDisplayName(profile, user?.email),
      [profile, user?.email]
    )

    // When handlerOverride (e.g. Sidekick) is present, use local state so input is scoped to that UI.
    const [scopedUserInput, setScopedUserInput] = useScopedUserInput()
    const [localUserInput, setLocalUserInput] = useState('')
    const [overrideInput, setOverrideInput] = useState('')
    const hadUserInputRef = useRef(false)
    const bootstrapDraftHandledRef = useRef(false)

    const userInput = handlerOverride
      ? overrideInput
      : chatbotUIContext
        ? scopedUserInput
        : localUserInput
    const setUserInput = handlerOverride
      ? setOverrideInput
      : chatbotUIContext
        ? setScopedUserInput
        : setLocalUserInput
    const [isInputReady, setIsInputReady] = useState(true) // Don't block input
    const legacyCrmMentionWorkspaceId =
      handlerOverride?.crmMentionWorkspaceId ?? selectedWorkspace?.id ?? null
    const isUnifiedMentionMode = !handlerOverride && !isPublic
    const isStructuredMentionMode = Boolean(
      handlerOverride?.mentionConfig ||
        (handlerOverride?.useCrmMentions && legacyCrmMentionWorkspaceId) ||
        isUnifiedMentionMode
    )
    const isGroupChat = Boolean(selectedChatForInput?.is_group_chat)
    const groupResponseMode = isGroupChat
      ? getGroupChatResponseMode(selectedChatForInput?.metadata)
      : 'always'
    const isGroupMentionOnlyMode = isGroupChat && groupResponseMode === 'mention_only'
    const isGroupAlwaysMode = isGroupChat && groupResponseMode === 'always'
    const isAssistantAutocompleteEnabled = shouldEnableChatInputAssistantAutocomplete({
      isGroupChat,
      enableGroupHumanMentions: isGroupAlwaysMode,
      isGroupMentionOnlyMode,
      isStructuredMentionMode
    })
    const hideToolControlsForOverride =
      Boolean(handlerOverride) && !handlerOverride?.keepToolControlsVisible
    const typingPresenceEnabled =
      realtimeEnabled &&
      Boolean(chatId && user?.id && hasResolvedAuthSession && !isPublic) &&
      (!handlerOverride || handlerOverride.enableTypingPresence === true)
    const { remoteTypers, setLocalTyping } = useChatTypingPresence({
      chatId: chatId ?? null,
      displayName: typingDisplayName,
      enabled: typingPresenceEnabled,
      userId: user?.id ?? null
    })

    // Remove tool loading dependency
    useEffect(() => {
      setIsInputReady(true)
    }, [])

    useEffect(() => {
      if (isAssistantAutocompleteEnabled) {
        return
      }

      setAutocompleteMention(null)
      setAutocompleteSuggestion('')
      setAtCommand('')
      setIsAssistantPickerOpen(false)
    }, [isAssistantAutocompleteEnabled, setAtCommand, setIsAssistantPickerOpen])

    useEffect(() => {
      if (!selectedChatForInput?.id || !isGroupChat) {
        setGroupMemberMentionOptions([])
        setGroupMemberMentionTokensReady(false)
        return
      }

      const cacheKey = getGroupMemberMentionCacheKey(selectedChatForInput.id, user?.id ?? null)
      const cachedOptions = readCachedGroupMemberMentions(cacheKey)

      if (cachedOptions) {
        setGroupMemberMentionOptions(cachedOptions)
        setGroupMemberMentionTokensReady(true)
        return
      }

      setGroupMemberMentionTokensReady(false)
      let cancelled = false
      const timeoutId = window.setTimeout(() => {
        void loadGroupMemberMentions(selectedChatForInput.id, user?.id ?? null)
          .then((options) => {
            if (cancelled) return

            setGroupMemberMentionOptions(options)
            setGroupMemberMentionTokensReady(true)
          })
          .catch(() => {
            if (!cancelled) {
              setGroupMemberMentionOptions([])
              setGroupMemberMentionTokensReady(false)
            }
          })
      }, 0)

      return () => {
        cancelled = true
        window.clearTimeout(timeoutId)
      }
    }, [isGroupChat, selectedChatForInput?.id, user?.id])

    useEffect(() => {
      if (!selectedWorkspace?.id || isGroupChat) {
        setWorkspaceMemberMentionOptions([])
        return
      }

      const cacheKey = getWorkspaceMemberMentionCacheKey(selectedWorkspace.id, user?.id ?? null)
      const cachedOptions = readCachedWorkspaceMemberMentions(cacheKey)

      if (cachedOptions) {
        setWorkspaceMemberMentionOptions(cachedOptions)
        return
      }

      let cancelled = false
      const timeoutId = window.setTimeout(() => {
        void loadWorkspaceMemberMentions(selectedWorkspace.id, user?.id ?? null)
          .then((options) => {
            if (!cancelled) {
              setWorkspaceMemberMentionOptions(options)
            }
          })
          .catch(() => {
            if (!cancelled) {
              setWorkspaceMemberMentionOptions([])
            }
          })
      }, 0)

      return () => {
        cancelled = true
        window.clearTimeout(timeoutId)
      }
    }, [isGroupChat, selectedWorkspace?.id, user?.id])

    const assistantMentionOptions = useMemo(
      () => assistants.map((assistant) => toAssistantMentionOption(assistant)),
      [assistants]
    )

    // Staffed agents are mentionable in normal chat: @-mention one to open a
    // direct staffing thread, or @-mention several to spin up / extend a
    // multi-agent group chat. Fetched from the agent-staffing configs.
    const [staffingAgents, setStaffingAgents] = useState<
      Array<{ agent_id: string; name: string; image_url: string | null; role: string | null }>
    >([])

    useEffect(() => {
      const workspaceId = selectedWorkspace?.id
      if (!workspaceId) {
        setStaffingAgents([])
        return
      }

      let cancelled = false
      void fetch(`/api/nexus/agent-staffing?workspace_id=${workspaceId}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (cancelled || !data) return
          const configs = Array.isArray(data.configs) ? data.configs : []
          setStaffingAgents(
            configs.map(
              (config: {
                agent_id: string
                template?: string | null
                name?: string | null
                image_url?: string | null
                role?: string | null
              }) => ({
                agent_id: config.agent_id,
                name: config.name ?? config.template ?? 'Agent',
                image_url: config.image_url ?? null,
                role: config.role ?? null
              })
            )
          )
        })
        .catch(() => {
          if (!cancelled) setStaffingAgents([])
        })

      return () => {
        cancelled = true
      }
    }, [selectedWorkspace?.id])

    const staffingAgentMentionOptions = useMemo<UnifiedMentionOption[]>(
      () =>
        staffingAgents.map((agent) => ({
          kind: 'staffing_agent' as const,
          id: agent.agent_id,
          token: agent.name,
          label: agent.name,
          description: agent.role ?? 'Staffed agent',
          imageUrl: agent.image_url
        })),
      [staffingAgents]
    )
    const unifiedMentionOptions = useMemo<UnifiedMentionOption[]>(() => {
      const humanMentionOptions = isGroupChat
        ? groupMemberMentionOptions
        : workspaceMemberMentionOptions

      return [
        LOSI_PLATFORM_MENTION_OPTION,
        ...LOSI_SUPER_AGENT_MENTION_OPTIONS,
        ...(isGroupChat ? [EVERYONE_MENTION_OPTION] : []),
        ...humanMentionOptions,
        ...staffingAgentMentionOptions,
        ...assistantMentionOptions
      ]
    }, [
      assistantMentionOptions,
      groupMemberMentionOptions,
      isGroupChat,
      staffingAgentMentionOptions,
      workspaceMemberMentionOptions
    ])
    const unifiedMentionConfig = useMemo<StructuredMentionConfig | null>(() => {
      if (!isUnifiedMentionMode) {
        return null
      }

      return {
        mode: 'unified-chat',
        markup: '@[__display__](__id__)',
        trigger: '@',
        allowEmptyQuery: true,
        getSuggestions: async (query: string) =>
          filterUnifiedMentionOptions(unifiedMentionOptions, query).map((option) => ({
            avatarUrl:
              option.kind === 'human' || option.kind === 'staffing_agent'
                ? option.imageUrl
                : null,
            category:
              option.kind === 'platform'
                ? 'Platform'
                : option.kind === 'super_agent'
                  ? 'Losi Super Agents'
                  : option.kind === 'assistant'
                    ? 'Assistants'
                    : option.kind === 'everyone'
                      ? 'Group'
                      : option.kind === 'staffing_agent'
                        ? 'Staffed agents'
                        : 'Contacts',
            description: option.description,
            id:
              option.kind === 'platform'
                ? 'losi:platform'
                : option.kind === 'super_agent'
                  ? `super-agent:${option.id}`
                  : option.kind === 'everyone'
                    ? 'everyone:all'
                    : option.kind === 'staffing_agent'
                      ? `staffing_agent:${option.id}`
                      : `${option.kind === 'assistant' ? 'assistant' : 'member'}:${option.id}`,
            kind:
              option.kind === 'human'
                ? 'member'
                : option.kind === 'everyone'
                  ? 'everyone'
                  : option.kind,
            display: option.label
          }))
      }
    }, [isUnifiedMentionMode, unifiedMentionOptions])
    const activeStructuredMentionConfig = handlerOverride?.mentionConfig ?? unifiedMentionConfig

    // Floating selected-text AI toolbar (outside Tiptap editor)
    // Always call hooks unconditionally
    const selectionResult = useSelectionInput()
    const selectionInputVisible = chatbotUIContext
      ? selectionResult?.selectionInputVisible || false
      : false
    const selectionPosition = chatbotUIContext ? selectionResult?.selectionPosition || null : null
    const selectedText = chatbotUIContext ? selectionResult?.selectedText || null : null

    // Compute active note from latest assistant/user message starting with #note=...#
    const activeNote = useMemo(() => {
      for (let i = effectiveChatMessages.length - 1; i >= 0; i--) {
        const m = effectiveChatMessages[i]
        const content = m?.message?.content || ''
        if (REGEX_NOTE.test(content)) {
          const lines = content.split(/\r?\n/)
          const header = lines[0] || ''
          const titleMatch = header.match(REGEX_NOTE)
          const title = titleMatch?.[1]?.trim() || 'Note'
          const body = lines.slice(1).join('\n').trim()
          return { title, body, messageId: m.message?.id }
        }
      }
      return null
    }, [effectiveChatMessages])

    // Track window width for responsive placeholder
    useEffect(() => {
      const handleResize = () => {
        setWindowWidth(window.innerWidth)
      }
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
      }
    }, [])

    // Override placeholder list when provided (e.g. Sidekick)
    const getPlaceholderTexts = useMemo(() => {
      if (placeholderTexts?.length) {
        return placeholderTexts
      }
      if (handlerOverride?.placeholderTexts?.length) {
        return handlerOverride.placeholderTexts
      }
      if (isGroupAlwaysMode) {
        return [
          'Message the group, or @someone to keep Losi out',
          'Tag a teammate for a human-only reply',
          'Ask Losi normally, tag people when it is for them'
        ]
      }
      if (isGroupMentionOnlyMode) {
        return [
          'Mention @Losi to get a reply',
          'Talk normally with teammates, tag Losi when needed'
        ]
      }
      if (isPublic) {
        return [
          'Ask anything',
          'Start by typing',
          'Edit image',
          'Create video',
          'Search the web',
          'Write code',
          'Fact check with sources',
          'Plan out a project',
          'Brainstorm ideas',
          'Visualize and analyze data',
          'Create content',
          'Get help',
          'Generate quizzes and flashcards'
        ]
      }
      if (windowWidth < 768) {
        return [
          t('chat_input.mobile_placeholder'),
          'Ask anything',
          'Search the web',
          'Start by typing',
          'Edit image',
          'Create video',
          'Search the web',
          'Write code',
          'Fact check with sources',
          'Plan out a project',
          'Brainstorm ideas',
          'Visualize and analyze data',
          'Create content',
          'Get help',
          'Generate quizzes and flashcards'
        ]
      }
      if (windowWidth < 1280) {
        return [
          t('chat_input.tablet_placeholder'),
          'Ask anything',
          'Generate images',
          'Write code',
          'Start by typing',
          'Edit image',
          'Create video',
          'Search the web',
          'Write code',
          'Fact check with sources',
          'Plan out a project',
          'Brainstorm ideas',
          'Visualize and analyze data',
          'Create content',
          'Get help',
          'Generate quizzes and flashcards'
        ]
      }
      return [
        t('chat_input.desktop_placeholder'),
        'Ask anything',
        'Search the web',
        'Start by typing',
        'Edit image',
        'Create video',
        'Search the web',
        'Write code',
        'Fact check with sources',
        'Plan out a project',
        'Brainstorm ideas',
        'Visualize and analyze data',
        'Create content',
        'Get help',
        'Generate quizzes and flashcards'
      ]
    }, [
      placeholderTexts,
      handlerOverride?.placeholderTexts,
      isGroupAlwaysMode,
      isGroupMentionOnlyMode,
      isPublic,
      windowWidth,
      t
    ])

    const [placeholderIndex, setPlaceholderIndex] = useState<number>(0)
    const placeholderText = getPlaceholderTexts[placeholderIndex] || getPlaceholderTexts[0]
    const [displayedChars, setDisplayedChars] = useState<number>(0)
    const [isTypingIn, setIsTypingIn] = useState<boolean>(true)

    // Stop animation once conversation has started (when there are messages)
    const hasConversationStarted = useMemo(() => {
      return effectiveChatMessages && effectiveChatMessages.length > 0
    }, [effectiveChatMessages])

    // Reset displayed chars when placeholder text changes (but not when conversation has started)
    useEffect(() => {
      if (!hasConversationStarted) {
        setDisplayedChars(0)
        setIsTypingIn(true)
      }
    }, [placeholderText, hasConversationStarted])

    // Reset to first placeholder when conversation starts
    useEffect(() => {
      if (hasConversationStarted) {
        setPlaceholderIndex(0)
        const firstPlaceholder = getPlaceholderTexts[0] || ''
        setDisplayedChars(firstPlaceholder.length) // Show full first placeholder
        setIsTypingIn(true)
      }
    }, [hasConversationStarted, getPlaceholderTexts])

    // Ensure placeholder stays visible when conversation has started
    useEffect(() => {
      if (hasConversationStarted && placeholderText && displayedChars !== placeholderText.length) {
        setDisplayedChars(placeholderText.length)
      }
    }, [hasConversationStarted, placeholderText, displayedChars])

    // Typewriter effect: type in -> pause -> backspace out -> next placeholder

    useEffect(() => {
      if (!userInput && placeholderText && !hasConversationStarted) {
        let timeoutId: NodeJS.Timeout

        if (isTypingIn) {
          // Typing in: add characters one by one
          if (displayedChars < placeholderText.length) {
            timeoutId = setTimeout(() => {
              setDisplayedChars((prev) => prev + 1)
            }, 50) // Type each character every 50ms
          } else {
            // Finished typing, pause then start backspacing
            timeoutId = setTimeout(() => {
              setIsTypingIn(false)
            }, 3000) // Pause for 3 seconds
          }
        } else {
          // Backspacing out: remove characters one by one
          if (displayedChars > 0) {
            timeoutId = setTimeout(() => {
              setDisplayedChars((prev) => prev - 1)
            }, 30) // Backspace each character every 30ms (faster)
          } else {
            // Finished backspacing, move to next placeholder
            setPlaceholderIndex((prev) => (prev + 1) % getPlaceholderTexts.length)
            setIsTypingIn(true)
          }
        }

        return () => {
          if (timeoutId) clearTimeout(timeoutId)
        }
      } else {
        // Reset when user starts typing or conversation has started
        setDisplayedChars(0)
        setIsTypingIn(true)
        setPlaceholderIndex(0)
      }
    }, [
      userInput,
      placeholderText,
      displayedChars,
      isTypingIn,
      getPlaceholderTexts,
      hasConversationStarted,
      effectiveChatMessages
    ])

    const handlerResult = useChatHandler({ chatId, forceDraftChat })
    const chatInputRef = handlerOverride
      ? handlerOverride.chatInputRef
      : chatbotUIContext
        ? handlerResult?.chatInputRef || null
        : null
    let handleSendMessageInternal = handlerOverride
      ? handlerOverride.handleSendMessage
      : chatbotUIContext
        ? handlerResult?.handleSendMessage || handleSendMessage
        : handleSendMessage
    const handleStopMessage = handlerOverride
      ? handlerOverride.handleStopMessage
      : propHandleStopMessage
        ? propHandleStopMessage
        : chatbotUIContext
          ? handlerResult?.handleStopMessage || (() => {})
          : () => {}
    const handleFocusChatInput =
      handlerOverride?.handleFocusChatInput ??
      (chatbotUIContext
        ? handlerResult?.handleFocusChatInput
        : undefined) ??
      // Module-stable noop — never allocate `() => {}` per render (effect deps).
      noopFocusChatInput
    const advancedVoiceMode = chatbotUIContext ? handlerResult?.advancedVoiceMode || null : null
    const handleStartAdvancedVoiceMode = chatbotUIContext
      ? handlerResult?.handleStartAdvancedVoiceMode || (() => {})
      : () => {}
    const handleConfirmAdvancedVoiceMode = chatbotUIContext
      ? handlerResult?.handleConfirmAdvancedVoiceMode || (() => {})
      : () => {}
    const handleEndAdvancedVoiceMode = chatbotUIContext
      ? handlerResult?.handleEndAdvancedVoiceMode || (() => {})
      : () => {}
    // Chat realtime is GA — show the wave for every signed-in chat (not public).
    const showChatRealtime = !isPublic

    useEffect(() => {
      if (!showChatRealtime && advancedVoiceMode) {
        handleEndAdvancedVoiceMode()
      }
    }, [advancedVoiceMode, handleEndAdvancedVoiceMode, showChatRealtime])

    const onTranscriptUpdate = chatbotUIContext
      ? handlerResult?.onTranscriptUpdate ||
        ((_transcript: string, _role?: 'user' | 'assistant') => {})
      : (_transcript: string, _role?: 'user' | 'assistant') => {}
    const onRealtimeInferenceEvent = chatbotUIContext
      ? handlerResult?.onRealtimeInferenceEvent || ((_event: unknown) => {})
      : (_event: unknown) => {}
    if (!handleSendMessage) {
      handleSendMessage = handleSendMessageInternal as ChatInputProps['handleSendMessage']
    }

    // Consume a draft typed in the workspace loading shell. It was
    // written to sessionStorage while GlobalState gated this
    // composer behind "Loading your workspace". Restore it here;
    // auto-send only on a brand-new chat (no chat/conversation
    // context to get wrong) — everywhere else the queued text is
    // restored and the user presses Enter in the real composer.
    useEffect(() => {
      if (bootstrapDraftHandledRef.current) return
      if (isPublic) {
        bootstrapDraftHandledRef.current = true
        return
      }

      const pending = readWorkspaceBootstrapDraft()
      if (!pending) {
        bootstrapDraftHandledRef.current = true
        return
      }

      // Never clobber a draft already in the real composer.
      if (userInput) return

      bootstrapDraftHandledRef.current = true
      clearWorkspaceBootstrapDraft()

      const canAutoSend =
        pending.queuedSend &&
        !handlerOverride &&
        !chatId &&
        !isComposerBusy &&
        Boolean(handleSendMessageInternal)

      if (!canAutoSend) {
        setUserInput(pending.text)
        if (pending.queuedSend) {
          toast.info('Your queued message is ready — press Enter to send.')
        }
        return
      }

      void Promise.resolve(
        handleSendMessageInternal!(pending.text, [...effectiveChatMessages], true)
      ).catch(() => {
        // Send failed after load: give the text back, never lose it.
        setUserInput(pending.text)
        toast.error('Could not send your queued message — it is back in the composer.')
      })
      // Mount-once: all inputs above are read at first ready render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Sync handler focus requests to the actual textarea.
    useLayoutEffect(() => {
      if (!chatInputRef) return
      const el = inputRef.current
      if (el) {
        ;(chatInputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el
        return () => {
          ;(chatInputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = null
        }
      }
    }, [chatInputRef])

    // Listen for sendPrompt events from editors/toolbars
    useEffect(() => {
      const handler = (e: any) => {
        const prompt = e?.detail?.prompt ?? e?.detail
        if (!prompt || !handleSendMessage) return

        if (isComposerBusy) {
          toast.info('Please wait for the current response to finish.')
          return
        }

        const notePrefix = activeNote
          ? `NOTE CONTEXT (Title: ${activeNote.title})\n"""\n${activeNote.body}\n"""\n\n`
          : ''
        const hasFiles =
          (effectiveNewMessageImages?.length ?? 0) > 0 ||
          (effectiveNewMessageFiles?.length ?? 0) > 0 ||
          (effectiveChatFiles?.length ?? 0) > 0 ||
          (effectiveChatFileItems?.length ?? 0) > 0
        const options: ChatSendOptions = {
          ...(isGroupAlwaysMode ? { groupMemberMentionTokens, groupMemberMentionTokensReady } : {}),
          hasAttachments: hasFiles
        }
        handleSendMessage(notePrefix + prompt, [...effectiveChatMessages], true, options)
      }
      document.addEventListener('sendPrompt' as any, handler)
      return () => document.removeEventListener('sendPrompt' as any, handler)
    }, [
      handleSendMessage,
      effectiveChatMessages,
      activeNote,
      effectiveNewMessageFiles,
      effectiveNewMessageImages,
      effectiveChatFiles,
      effectiveChatFileItems,
      isGroupAlwaysMode,
      isComposerBusy,
      groupMemberMentionTokensReady,
      groupMemberMentionTokens
    ])

    // Listen for populateInput events (Ask Losi action)
    useEffect(() => {
      const handler = (e: any) => {
        const prompt = e?.detail?.prompt ?? e?.detail
        if (!prompt) return

        // Extract selected text from quoted prompt
        const quotedMatch = prompt.match(/^"(.+)"\s*$/)
        if (quotedMatch) {
          setSelectedTextContext(quotedMatch[1])
          setUserInput('')
        } else {
          setUserInput(prompt)
        }

        // Focus the input after populating
        setTimeout(() => {
          if (
            !canFocusComposer ||
            shouldPreserveMessageTextSelection() ||
            shouldPreserveComputerInteractionFocus() ||
            hasActiveOverlay()
          ) {
            return
          }
          if (inputRef.current) {
            inputRef.current.focus()
            inputRef.current.setSelectionRange(
              inputRef.current.value.length,
              inputRef.current.value.length
            )
          }
        }, 100)
      }
      document.addEventListener('populateInput' as any, handler)
      return () => document.removeEventListener('populateInput' as any, handler)
    }, [canFocusComposer, setUserInput])

    // Always call hooks unconditionally
    const promptResult = usePromptAndCommand({ chatId })
    const handleInputChange = handlerOverride
      ? (value: string) => setUserInput(value)
      : chatbotUIContext
        ? promptResult?.handleInputChange || ((value: string) => setUserInput(value))
        : (value: string) => setUserInput(value)
    const handleSelectAssistant = chatbotUIContext
      ? promptResult?.handleSelectAssistant ||
        (async (_assistant: Tables<'assistants'>, _options?: { preserveInput?: boolean }) => {})
      : async (_assistant: Tables<'assistants'>, _options?: { preserveInput?: boolean }) => {}
    // @Assistant = invite to reply-as participant (no context swap).
    // Legacy "Use as active assistant" still calls handleSelectAssistant explicitly.
    const handleSelectAssistantMention = useCallback(
      (assistant: Tables<'assistants'>) => {
        setSelectedAssistantAnimation(assistant)
        setTimeout(() => setSelectedAssistantAnimation(null), 2000)
      },
      []
    )

    const persistVoiceInputDevice = useCallback((deviceId: string | null) => {
      selectedVoiceInputDeviceIdRef.current = deviceId
      setSelectedVoiceInputDeviceId(deviceId)
      persistPreferredAudioInputDeviceId(deviceId)
    }, [])

    const refreshVoiceInputDevices = useCallback(async () => {
      try {
        const devices = await listAudioInputDevices()
        setVoiceInputDevices(devices)
        return devices
      } catch (error) {
        safeConsole.warn('Unable to list voice input devices:', error)
        return []
      }
    }, [])

    // Handle file selection hook
    const activeChatSettings = handlerOverride?.chatSettings ?? chatSettings
    const imagesSupportedByActiveModel = useMemo(
      () =>
        LLM_LIST.find(
          (llm) =>
            llm.modelId === activeChatSettings?.model ||
            llm.hostedId === activeChatSettings?.model
        )?.imageInput ?? true,
      [activeChatSettings?.model]
    )
    const selectedChatWorkspaceUploadLimitChatId =
      getWorkspaceUploadLimitChatId(selectedChatForInput)
    const workspaceUploadLimitChatId =
      propWorkspaceUploadLimitChatId ?? selectedChatWorkspaceUploadLimitChatId
    const workspaceUploadLimitSpaceId = propWorkspaceUploadLimitSpaceId ?? null
    const fileWorkspaceId =
      propFileWorkspaceId ??
      (selectedChatWorkspaceUploadLimitChatId
        ? selectedChatForInput?.workspace_id
        : null)
    const fileResult = useSelectFileHandler({
      chatId,
      fileWorkspaceId,
      chatSettingsOverride: handlerOverride?.chatSettings ?? null,
      uploadLimitChatId: workspaceUploadLimitChatId,
      uploadLimitSpaceId: workspaceUploadLimitSpaceId,
      isPublic
    })
    const filesToAccept = chatbotUIContext ? fileResult?.filesToAccept || '' : ''
    const handleSelectDeviceFile = chatbotUIContext
      ? fileResult?.handleSelectDeviceFile || (() => {})
      : () => {}
    const handleSelectDeviceFiles = chatbotUIContext
      ? fileResult?.handleSelectDeviceFiles || (() => Promise.resolve())
      : () => Promise.resolve()
    const isUploading = chatbotUIContext ? fileResult?.isUploading || false : false
    const uploadStatus = chatbotUIContext ? fileResult?.uploadStatus ?? null : null
    const processingStatus = chatbotUIContext ? fileResult?.processingStatus || null : null
    const uploadStatusLabel =
      uploadStatus?.label ||
      processingStatus ||
      (isUploading ? t('chat.uploading_attachment', 'Uploading...') : null)
    const uploadProgressPercent = getUploadProgressPercent(uploadStatus, isUploading)
    const uploadProgressSize = formatUploadFileSize(uploadStatus?.fileSizeBytes)
    const uploadProgressDegrees = uploadProgressPercent * 3.6
    const uploadStatusFileName = uploadStatus?.fileName?.trim() || ''
    const uploadAccessibleLabel = uploadStatusFileName
      ? `${uploadStatusLabel} ${uploadStatusFileName}`
      : uploadStatusLabel
    const uploadProgressValueText = [
      uploadStatusLabel,
      `${uploadProgressPercent}%`,
      uploadProgressSize
    ]
      .filter(Boolean)
      .join(', ')

    // Handle chat history hook
    const historyResult = useChatHistoryHandler()
    const setNewMessageContentToNextUserMessage: () => void = chatbotUIContext
      ? historyResult?.setNewMessageContentToNextUserMessage || (() => {})
      : () => {}
    const setNewMessageContentToPreviousUserMessage: () => void = chatbotUIContext
      ? historyResult?.setNewMessageContentToPreviousUserMessage || (() => {})
      : () => {}

    const [quotedText, setQuotedText] = useState<QuotedTextData | null>(null)
    const [selectedTextContext, setSelectedTextContext] = useState<string | null>(null)
    const [editingQueuedId, setEditingQueuedId] = useState<string | null>(null)
    const [editingQueuedDraft, setEditingQueuedDraft] = useState('')
    const [detectedExtension, setDetectedExtension] = useState<any>(null)
    const spaceItemContext = handlerOverride?.spaceItemContext ?? null

    // Stable refs so mount-once / event effects never re-subscribe on handler identity.
    const canFocusComposerRef = useRef(canFocusComposer)
    const handleFocusChatInputRef = useRef(handleFocusChatInput)
    const chatInputRefForFocus = useRef(chatInputRef)
    canFocusComposerRef.current = canFocusComposer
    handleFocusChatInputRef.current = handleFocusChatInput
    chatInputRefForFocus.current = chatInputRef

    // Focus input only after the user chooses Reply on selected text
    useEffect(() => {
      const handleQuoteText = (e: CustomEvent) => {
        // Only accept quote events for this chat (prevents cross-pane in split view)
        if (e.detail.chatId && chatId && e.detail.chatId !== chatId) return

        setQuotedText({
          text: e.detail.text,
          messageId: e.detail.messageId
        })
        // Focus input when quote is set so user can type immediately
        setTimeout(() => {
          if (
            !canFocusComposerRef.current ||
            shouldPreserveMessageTextSelection() ||
            shouldPreserveComputerInteractionFocus() ||
            hasActiveOverlay()
          ) {
            return
          }
          if (chatInputRefForFocus.current) handleFocusChatInputRef.current()
          else inputRef.current?.focus()
        }, 0)
      }

      window.addEventListener('quoteText' as any, handleQuoteText)
      return () => window.removeEventListener('quoteText' as any, handleQuoteText)
    }, [chatId])

    // Focus input after a preview panel (e.g. Sidecar) is opened from an
    // inline message action, so the user can type immediately instead of
    // having to click back into the composer.
    useEffect(() => {
      const handleFocusChatInputEvent = () => {
        setTimeout(() => {
          if (
            !canFocusComposerRef.current ||
            shouldPreserveMessageTextSelection() ||
            shouldPreserveComputerInteractionFocus() ||
            hasActiveOverlay()
          ) {
            return
          }
          if (chatInputRefForFocus.current) handleFocusChatInputRef.current()
          else inputRef.current?.focus()
        }, 0)
      }

      window.addEventListener('focusChatInput', handleFocusChatInputEvent)
      return () =>
        window.removeEventListener('focusChatInput', handleFocusChatInputEvent)
    }, [])

    useEffect(() => {
      // Mount-once device bootstrap. Do not depend on focus handlers — they used
      // to change identity every render and re-fire this effect → React #185.
      const supported = canRecordVoiceInput()
      setVoiceInputSupported(prev => (prev === supported ? prev : supported))

      const storedDeviceId = readPreferredAudioInputDeviceId()
      if (storedDeviceId) {
        selectedVoiceInputDeviceIdRef.current = storedDeviceId
        setSelectedVoiceInputDeviceId(prev =>
          prev === storedDeviceId ? prev : storedDeviceId
        )
        setPendingVoiceInputDeviceId(prev =>
          prev === storedDeviceId ? prev : storedDeviceId
        )
      }

      void refreshVoiceInputDevices()

      const mediaDevices =
        typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
      const handleDeviceChange = () => {
        void refreshVoiceInputDevices()
      }

      mediaDevices?.addEventListener?.('devicechange', handleDeviceChange)

      // Only focus input once on mount so cursor is ready for the user to type
      const timer = setTimeout(() => {
        if (initialFocusAppliedRef.current) return
        initialFocusAppliedRef.current = true

        if (
          !canFocusComposerRef.current ||
          shouldPreserveMessageTextSelection() ||
          shouldPreserveComputerInteractionFocus() ||
          hasActiveOverlay()
        ) {
          return
        }

        if (chatInputRefForFocus.current) {
          handleFocusChatInputRef.current()
        } else {
          inputRef.current?.focus()
        }
      }, 200)

      return () => {
        clearTimeout(timer)
        mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange)
      }
    }, [refreshVoiceInputDevices])

    useEffect(() => {
      if (!listening) {
        setUserInputBeforeRecording(userInput)
      }
    }, [listening, userInput])

    // Memoize the input change handler to prevent recreation
    const handleInputChangeWithAutocomplete = useCallback(
      (value: string) => {
        if (!isInputReady) return

        // Structured mentions use their own picker.
        if (!isAssistantAutocompleteEnabled) {
          handleInputChange(value, {
            enableAssistantCommand: false
          })
          setAutocompleteMention(null)
          setAutocompleteSuggestion('')
          setIsAssistantPickerOpen(false)
          setAtCommand('')
          return
        }

        handleInputChange(value, {
          enableAssistantCommand: false
        })

        // Check for extension detection
        const detection = detectExtensionInMessage(value)
        if (detection.detected && value.trim().length > 3) {
          setDetectedExtension(detection)
        } else {
          setDetectedExtension(null)
        }

        // Only process mention autocomplete if we have a match
        const match = value.match(/@(\w*)$/)
        if (match) {
          const searchTerm = match[1].toLowerCase()
          const suggestion = getFirstUnifiedMentionOption(unifiedMentionOptions, searchTerm)

          setAtCommand(searchTerm)
          setIsAssistantPickerOpen(true)

          if (suggestion) {
            setAutocompleteMention(suggestion)
            setAutocompleteSuggestion(suggestion.token.slice(searchTerm.length))
          } else {
            setAutocompleteMention(null)
            setAutocompleteSuggestion('')
          }
        } else {
          setAutocompleteMention(null)
          setAutocompleteSuggestion('')
          setAtCommand('')
          setIsAssistantPickerOpen(false)
        }
      },
      [
        handleInputChange,
        isAssistantAutocompleteEnabled,
        isInputReady,
        setAtCommand,
        setIsAssistantPickerOpen,
        unifiedMentionOptions,
        setUserInput
      ]
    )

    const handleSelectMentionOption = useCallback(
      (option: UnifiedMentionOption) => {
        const newValue = replaceActiveMention(userInput, option)

        setUserInput(newValue)
        setAutocompleteMention(null)
        setAutocompleteSuggestion('')
        setAtCommand('')
        setIsAssistantPickerOpen(false)

        if (option.kind === 'assistant') {
          handleSelectAssistantMention(option.assistant)
        }

        if (inputRef.current) {
          inputRef.current.value = newValue
          inputRef.current.focus()
          inputRef.current.setSelectionRange(newValue.length, newValue.length)
        }
      },
      [
        handleSelectAssistantMention,
        setAtCommand,
        setIsAssistantPickerOpen,
        setUserInput,
        userInput
      ]
    )

    const canQueueFollowUp = Boolean(
      isComposerBusy && onRemoveQueuedTurn && onUpdateQueuedTurn && onSendQueuedTurnNow
    )
    const hasDraftAttachments =
      (effectiveNewMessageImages?.length ?? 0) > 0 ||
      (effectiveNewMessageFiles?.length ?? 0) > 0 ||
      (effectiveChatFiles?.length ?? 0) > 0 ||
      (effectiveChatFileItems?.length ?? 0) > 0
    const canSendComposerMessage =
      userInput.trim().length > 0 ||
      Boolean(voiceMemo) ||
      Boolean(selectedTextContext?.trim()) ||
      hasDraftAttachments
    const syncComposerTypingState = useCallback(
      (value: string) => {
        const nextIsTyping = value.trim().length > 0

        setIsTyping(nextIsTyping)
        void setLocalTyping(nextIsTyping)
      },
      [setLocalTyping]
    )
    const stopComposerTyping = useCallback(() => {
      setIsTyping(false)
      void setLocalTyping(false, { force: true })
    }, [setLocalTyping])

    useEffect(() => {
      const hasUserInput = userInput.trim().length > 0

      if (hadUserInputRef.current && !hasUserInput) {
        stopComposerTyping()
      }

      hadUserInputRef.current = hasUserInput
    }, [stopComposerTyping, userInput])

    const isStopShortcut = useCallback(
      (event: React.KeyboardEvent) => {
        if (profile?.send_message_on_enter === false) {
          return event.key === 'Enter' && (event.ctrlKey || event.metaKey)
        }

        return event.key === 'Enter' && !event.shiftKey
      },
      [profile?.send_message_on_enter]
    )

    // Memoize send shortcut check
    const isSendShortcut = useCallback(
      (event: React.KeyboardEvent) => {
        if (isComposerBusy && !canQueueFollowUp) {
          return false
        }

        // Default behavior: Enter without Shift sends the message
        let shortcutPressed = event.key === 'Enter' && !event.shiftKey

        // If profile setting is explicitly false, require Ctrl/Cmd+Enter
        if (profile?.send_message_on_enter === false) {
          shortcutPressed = event.key === 'Enter' && (event.ctrlKey || event.metaKey)
        }

        // Debug logging
        // console.log('Send shortcut check:', {
        //   key: event.key,
        //   shift: event.shiftKey,
        //   ctrl: event.ctrlKey,
        //   meta: event.metaKey,
        //   sendOnEnter: profile?.send_message_on_enter,
        //   shortcutPressed
        // })

        return shortcutPressed
      },
      [
        canQueueFollowUp,
        isComposerBusy,
        profile?.send_message_on_enter,
      ]
    )

    // Memoize send handlers
    const handleSendWithQuote = useCallback((override?: VoiceMemoSendOverride) => {
      if (!handleSendMessage) return

      if (isComposerBusy && !canQueueFollowUp) {
        toast.info('Please wait for the current response to finish.')
        return
      }

      const draftUserInput = override?.userInput ?? userInput
      const draftTranscript = override?.transcript ?? transcript
      const draftVoiceMemo = override?.voiceMemo ?? voiceMemo
      const draftVoiceRecordingDurationSeconds =
        override?.voiceRecordingDurationSeconds ?? voiceRecordingDurationSeconds
      const draftVoiceWaveformSamples = override?.voiceWaveformSamples ?? voiceWaveformSamples
      let finalMessage = draftUserInput || inputRef.current?.value || ''
      const parsedProductCreationCommand = onProductCreationCommand
        ? parseChatProductCreationCommand(finalMessage)
        : null
      const productCreationCommand =
        parsedProductCreationCommand &&
        !shouldSuppressEngineProductCreationForContentEngine(finalMessage)
          ? parsedProductCreationCommand
          : null

      if (productCreationCommand) {
        const promptContext = [
          productCreationCommand.prompt,
          selectedTextContext ? `Selected context:\n${selectedTextContext}` : '',
          quotedText?.text ? `Quoted context:\n${quotedText.text}` : '',
          spaceItemContext
            ? `Space item context: ${spaceItemContext.type} - ${spaceItemContext.title}`
            : ''
        ]
          .filter(Boolean)
          .join('\n\n')
          .trim()

        onProductCreationCommand?.({
          ...productCreationCommand,
          prompt: promptContext,
          ...(productCreationCommand.kind === 'sites'
            ? { siteExperienceId: resolveSiteExperienceIdFromPrompt(promptContext) }
            : {})
        })
      }

      // Add selected text context if present
      if (selectedTextContext) {
        finalMessage = `"${selectedTextContext}"\n\n${draftUserInput}`.trim()
      }

      if (spaceItemContext) {
        finalMessage = `${serializeSidekickSpaceItemTag(spaceItemContext)}\n${finalMessage}`.trim()
      }

      if (draftVoiceMemo) {
        finalMessage = [finalMessage, formatVoiceMemoTranscriptForModel(draftVoiceMemo)]
          .filter(Boolean)
          .join('\n\n')
      }

      // Add quote if present
      if (quotedText && finalMessage.trim()) {
        finalMessage = `[QUOTE:${quotedText.messageId || ' '}]${quotedText.text}[/QUOTE]\n${finalMessage}`
      }

      const trimmedFinalMessage = finalMessage.trim()

      const hasFiles = hasDraftAttachments

      if (trimmedFinalMessage || hasFiles) {
        const notePrefix = activeNote
          ? `NOTE CONTEXT (Title: ${activeNote.title})\n"""\n${activeNote.body}\n"""\n\n`
          : ''
        let sendAccepted = false
        const sentDraft = {
          detectedExtension,
          quotedText,
          selectedTextContext,
          transcript: draftTranscript,
          voiceOriginalTranscript: voiceOriginalTranscriptRef.current,
          voiceMemo: draftVoiceMemo,
          voiceRecordingDurationSeconds: draftVoiceRecordingDurationSeconds,
          voiceWaveformSamples: draftVoiceWaveformSamples,
          userInput: draftUserInput,
          userInputBeforeRecording
        }
        const acceptSend = () => {
          if (sendAccepted) return

          sendAccepted = true
          if (inputRef.current) {
            inputRef.current.value = ''
          }
          stopComposerTyping()
          setQuotedText(null)
          setSelectedTextContext(null)
          setUserInput('')
          setTranscript('')
          setVoiceMemo(null)
          setVoiceRecordingDurationSeconds(null)
          setVoiceRecordingStartedAt(null)
          setVoiceWaveformSamples([])
          setUserInputBeforeRecording('')
          voiceWaveformSamplesRef.current = []
          voiceRecordingStartedAtRef.current = null
          voiceOriginalTranscriptRef.current = null
          if (voiceObjectUrlRef.current) {
            const sentObjectUrl = getLocalVoiceMemoObjectUrl(draftVoiceMemo)

            if (voiceObjectUrlRef.current !== sentObjectUrl) {
              URL.revokeObjectURL(voiceObjectUrlRef.current)
            }

            voiceObjectUrlRef.current = null
          }
          setDetectedExtension(null)
        }
        const rejectSend = () => {
          if (!sendAccepted) return

          sendAccepted = false
          setQuotedText(sentDraft.quotedText)
          setSelectedTextContext(sentDraft.selectedTextContext)
          setUserInput(sentDraft.userInput)
          setTranscript(sentDraft.transcript)
          setVoiceMemo(sentDraft.voiceMemo)
          setVoiceRecordingDurationSeconds(sentDraft.voiceRecordingDurationSeconds)
          setVoiceWaveformSamples(sentDraft.voiceWaveformSamples)
          voiceWaveformSamplesRef.current = sentDraft.voiceWaveformSamples
          voiceOriginalTranscriptRef.current = sentDraft.voiceOriginalTranscript
          setUserInputBeforeRecording(sentDraft.userInputBeforeRecording)
          voiceObjectUrlRef.current = getLocalVoiceMemoObjectUrl(sentDraft.voiceMemo)
          setDetectedExtension(sentDraft.detectedExtension)
        }
        const options: ChatSendOptions = {
          ...(isGroupAlwaysMode ? { groupMemberMentionTokens, groupMemberMentionTokensReady } : {}),
          hasAttachments: hasFiles,
          ...(draftVoiceMemo ? { voiceMemo: draftVoiceMemo } : {}),
          onAccepted: acceptSend,
          onRejected: rejectSend
        }

        try {
          const result = handleSendMessage!(
            notePrefix + finalMessage,
            effectiveChatMessages,
            false,
            options
          )

          void Promise.resolve(result).catch((error) => {
            safeConsole.error('Chat send failed before acceptance:', error)
          })
        } catch (error) {
          safeConsole.error('Chat send failed before acceptance:', error)
        }
      }
    }, [
      handleSendMessage,
      quotedText,
      spaceItemContext,
      selectedTextContext,
      detectedExtension,
      userInput,
      transcript,
      voiceMemo,
      voiceRecordingDurationSeconds,
      voiceWaveformSamples,
      userInputBeforeRecording,
      effectiveChatMessages,
      setQuotedText,
      setUserInput,
      setTranscript,
      setVoiceMemo,
      setUserInputBeforeRecording,
      activeNote,
      canQueueFollowUp,
      hasDraftAttachments,
      isComposerBusy,
      stopComposerTyping,
      onProductCreationCommand,
      isGroupAlwaysMode,
      groupMemberMentionTokensReady,
      groupMemberMentionTokens
    ])

    // Memoize key down handler
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isInputReady) return

        // Plan mode: Shift+Tab opens plan panel or starts plan flow
        if (event.key === 'Tab' && event.shiftKey && onPlanModeClick) {
          event.preventDefault()
          const content =
            userInput.trim() ||
            (effectiveChatMessages?.length
              ? String(
                  (effectiveChatMessages[effectiveChatMessages.length - 1] as any)?.message
                    ?.content ?? ''
                ).trim()
              : '') ||
            'Create an implementation plan for the current conversation.'
          onPlanModeClick(content)
          return
        }

        if (
          effectiveIsGenerating &&
          isStopShortcut(event) &&
          (!canQueueFollowUp || !canSendComposerMessage)
        ) {
          event.preventDefault()
          handleStopMessage()
          return
        }

        // Mention autocomplete (disabled while structured mentions are active)
        if (
          isAssistantAutocompleteEnabled &&
          autocompleteMention &&
          (event.key === ' ' || event.key === 'Tab')
        ) {
          event.preventDefault()
          handleSelectMentionOption(autocompleteMention)
          return
        }

        // When there are queued messages, Enter force-sends the oldest now.
        // This must work WHILE a response is generating — that is the point of
        // force-send. (Empty composer = send queue; typed text still queues/sends.)
        if (
          isSendShortcut(event) &&
          !sidecarRunActive &&
          !isUploading &&
          !isTranscribingVoice &&
          queuedSidecarTurns.length > 0 &&
          onSendQueuedTurnNow &&
          !canSendComposerMessage
        ) {
          event.preventDefault()
          const firstInQueue = queuedSidecarTurns[0]
          onSendQueuedTurnNow(firstInQueue.id)
          return
        }

        // Send message handling - modified to work regardless of isTyping state
        if (
          isSendShortcut(event) &&
          !isUploading &&
          !isTranscribingVoice &&
          canSendComposerMessage
        ) {
          event.preventDefault()
          setIsPromptPickerOpen(false)
          setTranscript('')
          setUserInputBeforeRecording('')
          handleSendWithQuote()
          return
        }

        // Picker focus handling
        if (isPromptPickerOpen || isFilePickerOpen || isToolPickerOpen || isAssistantPickerOpen) {
          if (event.key === 'Tab' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault()
            if (isPromptPickerOpen) setFocusPrompt(!focusPrompt)
            if (isFilePickerOpen) setFocusFile(!focusFile)
            if (isToolPickerOpen) setFocusTool(!focusTool)
            if (isAssistantPickerOpen) setFocusAssistant(!focusAssistant)
            return
          }
        }

        // Chat history navigation
        if (event.key === 'ArrowUp' && event.shiftKey && event.ctrlKey) {
          event.preventDefault()
          setNewMessageContentToPreviousUserMessage()
          return
        }

        if (event.key === 'ArrowDown' && event.shiftKey && event.ctrlKey) {
          event.preventDefault()
          setNewMessageContentToNextUserMessage()
          return
        }
      },
      [
        isInputReady,
        onPlanModeClick,
        userInput,
        effectiveChatMessages,
        autocompleteMention,
        handleSelectMentionOption,
        isTyping,
        canQueueFollowUp,
        canSendComposerMessage,
        effectiveIsGenerating,
        effectiveIsPersisting,
        isSendShortcut,
        isStopShortcut,
        isUploading,
        isTranscribingVoice,
        setIsPromptPickerOpen,
        setTranscript,
        setUserInputBeforeRecording,
        handleSendWithQuote,
        handleStopMessage,
        queuedSidecarTurns,
        onSendQueuedTurnNow,
        isPromptPickerOpen,
        isFilePickerOpen,
        isToolPickerOpen,
        isAssistantPickerOpen,
        setFocusPrompt,
        focusPrompt,
        setFocusFile,
        focusFile,
        setFocusTool,
        focusTool,
        setFocusAssistant,
        focusAssistant,
        setNewMessageContentToPreviousUserMessage,
        setNewMessageContentToNextUserMessage,
        sidecarRunActive,
        isStructuredMentionMode
      ]
    )

    const handlePaste = (event: React.ClipboardEvent) => {
      const items = event.clipboardData.items
      for (const item of items) {
        if (item.type.indexOf('image') === 0) {
          event.preventDefault()
          if (!imagesSupportedByActiveModel) {
            toast.error(
              `Images are not supported for this model. Use models like GPT-4 Vision instead.`
            )
            return
          }
          const file = item.getAsFile()
          if (!file) return
          handleSelectDeviceFile(file)
        }
      }
    }

    const stopVoiceWaveform = useCallback(() => {
      if (voiceSilenceWarningTimerRef.current !== null) {
        window.clearTimeout(voiceSilenceWarningTimerRef.current)
        voiceSilenceWarningTimerRef.current = null
      }

      if (voiceAnalyserFrameRef.current !== null) {
        window.cancelAnimationFrame(voiceAnalyserFrameRef.current)
        voiceAnalyserFrameRef.current = null
      }

      const audioContext = voiceAudioContextRef.current
      voiceAudioContextRef.current = null

      if (audioContext && audioContext.state !== 'closed') {
        void audioContext.close().catch(() => undefined)
      }
    }, [])

    const appendVoiceWaveformSample = useCallback((sample: number) => {
      const nextSamples = normalizeVoiceMemoWaveformSamples([
        ...voiceWaveformSamplesRef.current,
        sample
      ]).slice(-VOICE_WAVEFORM_SAMPLE_COUNT)

      voiceWaveformSamplesRef.current = nextSamples
      setVoiceWaveformSamples(nextSamples)
    }, [])

    const startVoiceWaveform = useCallback(
      (stream: MediaStream) => {
        stopVoiceWaveform()

        if (typeof window === 'undefined') {
          return
        }

        const AudioContextCtor =
          window.AudioContext ??
          (
            window as Window & {
              webkitAudioContext?: AudioContextConstructor
            }
          ).webkitAudioContext

        if (!AudioContextCtor) {
          return
        }

        try {
          const audioContext = new AudioContextCtor()
          const analyser = audioContext.createAnalyser()
          const source = audioContext.createMediaStreamSource(stream)
          let lastSampledAt = 0
          let smoothedLevel = 0

          analyser.fftSize = 512
          analyser.smoothingTimeConstant = 0.82
          const data = new Uint8Array(analyser.fftSize)
          source.connect(analyser)
          voiceAudioContextRef.current = audioContext
          if (audioContext.state === 'suspended') {
            void audioContext.resume().catch(() => undefined)
          }

          const sample = (timestamp: number) => {
            if (timestamp - lastSampledAt >= VOICE_WAVEFORM_SAMPLE_INTERVAL_MS) {
              analyser.getByteTimeDomainData(data)
              const { signalLevel, visualLevel } = readWaveformSignal(data)
              voiceSignalSampleCountRef.current += 1
              voiceSignalPeakRef.current = Math.max(voiceSignalPeakRef.current, signalLevel)
              if (signalLevel >= VOICE_SILENCE_PEAK_THRESHOLD) {
                setVoiceDevicePopoverOpen(false)
              }
              smoothedLevel =
                smoothedLevel * VOICE_WAVEFORM_SMOOTHING +
                visualLevel * (1 - VOICE_WAVEFORM_SMOOTHING)
              appendVoiceWaveformSample(smoothedLevel)
              lastSampledAt = timestamp
            }

            voiceAnalyserFrameRef.current = window.requestAnimationFrame(sample)
          }

          voiceAnalyserFrameRef.current = window.requestAnimationFrame(sample)
        } catch (error) {
          safeConsole.warn('Voice waveform failed to start:', error)
        }
      },
      [appendVoiceWaveformSample, stopVoiceWaveform]
    )

    const resetVoiceMemoDraft = useCallback(() => {
      setTranscript('')
      setVoiceMemo(null)
      setVoiceRecordingDurationSeconds(null)
      setVoiceRecordingStartedAt(null)
      setVoiceWaveformSamples([])
      setUserInputBeforeRecording('')
      voiceChunksRef.current = []
      voiceWaveformSamplesRef.current = []
      voiceRecordingStartedAtRef.current = null
      voiceCancelRecordingRef.current = false
      voiceStopRequestedRef.current = false
      voiceSignalPeakRef.current = 0
      voiceSignalSampleCountRef.current = 0
      activeVoiceInputDeviceIdRef.current = null
      voiceSendAfterStopRequestedRef.current = false
      voiceOriginalTranscriptRef.current = null

      if (voiceObjectUrlRef.current) {
        URL.revokeObjectURL(voiceObjectUrlRef.current)
        voiceObjectUrlRef.current = null
      }
    }, [])

    const isCurrentVoiceInputSilent = useCallback(() => {
      return (
        voiceSignalSampleCountRef.current >= VOICE_SILENCE_SAMPLE_MINIMUM &&
        voiceSignalPeakRef.current < VOICE_SILENCE_PEAK_THRESHOLD
      )
    }, [])

    const showSilentVoiceInputPopover = useCallback(
      async (activeDeviceId?: string | null) => {
        const devices = await refreshVoiceInputDevices()
        const resolvedActiveDeviceId =
          activeDeviceId ||
          activeVoiceInputDeviceIdRef.current ||
          selectedVoiceInputDeviceIdRef.current ||
          null
        const activeDeviceIds = new Set(
          [resolvedActiveDeviceId, selectedVoiceInputDeviceIdRef.current].filter(Boolean) as string[]
        )
        const alternatives = devices.filter((device) => {
          if (activeDeviceIds.has(device.deviceId)) {
            return false
          }

          return !(device.isDefault && Boolean(resolvedActiveDeviceId))
        })

        setSilentVoiceInputDeviceId(resolvedActiveDeviceId)
        setPendingVoiceInputDeviceId(alternatives[0]?.deviceId ?? devices[0]?.deviceId ?? null)
        setVoiceDevicePopoverOpen(true)
      },
      [refreshVoiceInputDevices]
    )

    const scheduleVoiceSilenceWarning = useCallback(
      (stream: MediaStream) => {
        if (voiceSilenceWarningTimerRef.current !== null) {
          window.clearTimeout(voiceSilenceWarningTimerRef.current)
        }

        voiceSilenceWarningTimerRef.current = window.setTimeout(() => {
          voiceSilenceWarningTimerRef.current = null

          const recorder = voiceRecorderRef.current
          if (!recorder || recorder.state === 'inactive' || voiceCancelRecordingRef.current) {
            return
          }

          if (!isCurrentVoiceInputSilent()) {
            return
          }

          const activeDeviceId =
            getAudioInputDeviceIdFromStream(stream) || activeVoiceInputDeviceIdRef.current

          void showSilentVoiceInputPopover(activeDeviceId)
        }, VOICE_SILENCE_WARNING_DELAY_MS)
      },
      [isCurrentVoiceInputSilent, showSilentVoiceInputPopover]
    )

    const discardVoiceMemoDraft = useCallback(() => {
      setVoiceDevicePopoverOpen(false)
      setSilentVoiceInputDeviceId(null)

      const recorder = voiceRecorderRef.current

      if (recorder && recorder.state !== 'inactive') {
        voiceCancelRecordingRef.current = true
        try {
          recorder.stop()
        } catch (error) {
          safeConsole.warn('Voice recorder discard stop failed:', error)
          voiceRecorderRef.current = null
          stopVoiceRecorderTracks()
          stopVoiceWaveform()
          resetVoiceMemoDraft()
        }
        return
      }

      resetVoiceMemoDraft()
    }, [resetVoiceMemoDraft])

    useEffect(() => {
      if (!listening || voiceRecordingStartedAt === null) {
        return
      }

      const updateDuration = () => {
        setVoiceRecordingDurationSeconds(Math.max(0, (Date.now() - voiceRecordingStartedAt) / 1000))
      }

      updateDuration()
      const timer = window.setInterval(updateDuration, 250)

      return () => window.clearInterval(timer)
    }, [listening, voiceRecordingStartedAt])

    useEffect(() => {
      return () => {
        voiceHoldReleaseCleanupRef.current?.()
        voiceHoldReleaseCleanupRef.current = null

        const recorder = voiceRecorderRef.current
        if (recorder) {
          recorder.ondataavailable = null
          recorder.onerror = null
          recorder.onstop = null

          if (recorder.state !== 'inactive') {
            recorder.stop()
          }
        }

        voiceRecorderRef.current = null
        voiceStreamRef.current?.getTracks().forEach((track) => track.stop())
        voiceStreamRef.current = null
        stopVoiceWaveform()

        if (voiceObjectUrlRef.current) {
          URL.revokeObjectURL(voiceObjectUrlRef.current)
        }
      }
    }, [stopVoiceWaveform])

    const transcribeVoiceInput = async (
      audioBlob: Blob,
      fallbackDurationSeconds?: number | null,
      restoredUserInput = userInputBeforeRecording,
      liveTranscript: string | null = null
    ) => {
      if (audioBlob.size === 0) {
        toast.error('No voice input was captured')
        return null
      }

      setIsTranscribingVoice(true)

      try {
        const waveformSamples = normalizeVoiceMemoWaveformSamples(
          voiceWaveformSamplesRef.current
        )
        const nextVoiceMemo = await transcribeRecordedVoiceMemo({
          audioBlob,
          fallbackDurationSeconds,
          isAuthenticated: Boolean(user),
          liveTranscript,
          waveformSamples
        })

        if (voiceObjectUrlRef.current) {
          URL.revokeObjectURL(voiceObjectUrlRef.current)
          voiceObjectUrlRef.current = null
        }

        if (nextVoiceMemo.audioPath === null && nextVoiceMemo.audioUrl.startsWith('blob:')) {
          voiceObjectUrlRef.current = nextVoiceMemo.audioUrl
        }

        setTranscript(nextVoiceMemo.transcript)
        setVoiceMemo(nextVoiceMemo)
        voiceOriginalTranscriptRef.current = nextVoiceMemo.transcript
        setVoiceWaveformSamples(nextVoiceMemo.waveformSamples)
        voiceWaveformSamplesRef.current = nextVoiceMemo.waveformSamples
        setUserInput(restoredUserInput)
        void discoverability?.markFeatureUsed({
          featureKey: 'voice_memos',
          surface: 'voice',
          trigger: 'voice_transcribed',
          metadata: {
            durationSeconds: nextVoiceMemo.durationSeconds ?? fallbackDurationSeconds ?? null
          }
        })
        return nextVoiceMemo
      } catch (error) {
        safeConsole.error('Voice transcription failed:', error)
        setVoiceMemo(null)
        // Mirror the text send-error behavior: fall back to the chat input
        // instead of wiping the composer. Restore whatever the user had typed
        // before recording so their draft is never silently lost on failure.
        setUserInput(restoredUserInput)
        toast.error(error instanceof Error ? error.message : 'Voice transcription failed')
        return null
      } finally {
        setIsTranscribingVoice(false)
      }
    }

    const updateVoiceMemoTranscript = useCallback(
      (nextTranscript: string) => {
        const currentVoiceMemo = voiceMemo
        if (!currentVoiceMemo) return

        const updatedVoiceMemo = updateChatVoiceMemoTranscript(currentVoiceMemo, nextTranscript)
        const originalTranscript = voiceOriginalTranscriptRef.current ?? currentVoiceMemo.transcript

        rememberVoiceNoteCorrection(originalTranscript, updatedVoiceMemo.transcript)
        setTranscript(updatedVoiceMemo.transcript)
        setVoiceMemo(updatedVoiceMemo)
      },
      [voiceMemo]
    )

    const primeVoiceInputPermissionPrompt = async () => {
      const permissionState = await getBrowserMicrophonePermissionState()

      if (permissionState !== 'prompt') {
        return
      }

      const stream = await getUserMediaWithNativePermissions(
        { audio: createAudioInputConstraints(null) },
        { requestSource: 'chat_voice_input_permission_prompt' }
      )

      stream.getTracks().forEach((track) => track.stop())
      void refreshVoiceInputDevices()
    }

    const openVoiceInputStream = async (deviceId?: string | null) => {
      const preferredDeviceId = deviceId ?? selectedVoiceInputDeviceIdRef.current

      if (preferredDeviceId) {
        await primeVoiceInputPermissionPrompt()
      }

      try {
        const stream = await getUserMediaWithNativePermissions(
          { audio: createAudioInputConstraints(preferredDeviceId) },
          { requestSource: 'chat_voice_input' }
        )

        activeVoiceInputDeviceIdRef.current = getAudioInputDeviceIdFromStream(stream)
        return stream
      } catch (error) {
        if (!preferredDeviceId || !isAudioInputDeviceUnavailableError(error)) {
          throw error
        }

        safeConsole.warn('Selected voice input device is unavailable; falling back to default:', error)
        persistVoiceInputDevice(null)
        setPendingVoiceInputDeviceId(null)

        const fallbackStream = await getUserMediaWithNativePermissions(
          { audio: createAudioInputConstraints(null) },
          { requestSource: 'chat_voice_input_fallback' }
        )

        activeVoiceInputDeviceIdRef.current = getAudioInputDeviceIdFromStream(fallbackStream)
        void refreshVoiceInputDevices()
        return fallbackStream
      }
    }

    const startListening = async (deviceIdOverride?: string | null) => {
      // Pointer-down and the trailing click can both ask to start; never run
      // two mic captures at once.
      if (voiceRecorderRef.current || voiceStartInFlightRef.current) {
        return
      }

      const canRecord = canRecordVoiceInput()
      setVoiceInputSupported(canRecord)

      if (!canRecord) {
        safeConsole.warn('Voice input is not supported in this browser')
        toast.error('Voice input is not supported in this browser')
        return
      }

      voiceStartInFlightRef.current = true

      try {
        const stream = await openVoiceInputStream(deviceIdOverride)
        const mimeType = getVoiceInputMimeType()
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
        const recordingStartedUserInput = userInput

        voiceChunksRef.current = []
        voiceSignalPeakRef.current = 0
        voiceSignalSampleCountRef.current = 0
        voiceStreamRef.current = stream
        voiceRecorderRef.current = recorder
        voiceStartInFlightRef.current = false
        resetVoiceMemoDraft()
        activeVoiceInputDeviceIdRef.current = getAudioInputDeviceIdFromStream(stream)
        setVoiceDevicePopoverOpen(false)
        setSilentVoiceInputDeviceId(null)
        setUserInputBeforeRecording(recordingStartedUserInput)
        voiceStopRequestedRef.current = false
        voiceSendAfterStopRequestedRef.current = false
        setVoiceRecordingDurationSeconds(0)
        const recordingStartedAt = Date.now()
        voiceRecordingStartedAtRef.current = recordingStartedAt
        setVoiceRecordingStartedAt(recordingStartedAt)
        startVoiceWaveform(stream)
        scheduleVoiceSilenceWarning(stream)
        void refreshVoiceInputDevices()

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            voiceChunksRef.current.push(event.data)
          }
        }

        recorder.onerror = (event) => {
          voiceCancelRecordingRef.current = true
          safeConsole.error('Voice recorder emitted an error:', event)
          toast.error('Voice recording failed')
          stopVoiceWaveform()
          setListening(false)

          if (recorder.state !== 'inactive') {
            try {
              recorder.stop()
              return
            } catch (error) {
              safeConsole.warn('Voice recorder cleanup failed:', error)
            }
          }

          voiceRecorderRef.current = null
          stream.getTracks().forEach((track) => track.stop())
          voiceStreamRef.current = null
          resetVoiceMemoDraft()
        }

        recorder.onstop = () => {
          const chunks = voiceChunksRef.current
          const type = recorder.mimeType || mimeType || 'audio/webm'
          const wasCancelled = voiceCancelRecordingRef.current
          const stopWasRequested = voiceStopRequestedRef.current
          const activeDeviceId =
            getAudioInputDeviceIdFromStream(stream) || activeVoiceInputDeviceIdRef.current
          const shouldOfferMicSwitch = !wasCancelled && isCurrentVoiceInputSilent()
          const shouldSendAfterStop = voiceSendAfterStopRequestedRef.current
          const recordedUserInput = recordingStartedUserInput
          voiceChunksRef.current = []
          voiceRecorderRef.current = null
          stream.getTracks().forEach((track) => track.stop())
          voiceStreamRef.current = null
          setListening(false)
          stopVoiceWaveform()

          const elapsedDurationSeconds =
            voiceRecordingStartedAtRef.current !== null
              ? Math.max(0.1, (Date.now() - voiceRecordingStartedAtRef.current) / 1000)
              : null

          setVoiceRecordingDurationSeconds(elapsedDurationSeconds)

          // Collect the live transcript (or nothing) before deciding the path.
          const liveTranscript = stopLiveDictation()

          if (wasCancelled) {
            resetVoiceMemoDraft()
            return
          }

          if (shouldOfferMicSwitch) {
            resetVoiceMemoDraft()
            setUserInput(recordedUserInput)
            void showSilentVoiceInputPopover(activeDeviceId)
            return
          }

          if (chunks.length === 0 && !stopWasRequested) {
            safeConsole.warn('Voice recorder stopped before audio data was captured')
            toast.error('Voice recording stopped before audio was captured')
            resetVoiceMemoDraft()
            setUserInput(recordedUserInput)
            return
          }

          void transcribeVoiceInput(
            new Blob(chunks, { type }),
            elapsedDurationSeconds,
            recordedUserInput,
            liveTranscript || null
          )
            .then((nextVoiceMemo) => {
              if (!nextVoiceMemo || !shouldSendAfterStop) {
                return
              }

              handleSendWithQuote({
                transcript: nextVoiceMemo.transcript,
                userInput: recordedUserInput,
                voiceMemo: nextVoiceMemo,
                voiceRecordingDurationSeconds: elapsedDurationSeconds,
                voiceWaveformSamples: nextVoiceMemo.waveformSamples
              })
            })
            .finally(() => {
              voiceSendAfterStopRequestedRef.current = false
            })
        }

        recorder.start(VOICE_RECORDER_CHUNK_INTERVAL_MS)
        setListening(true)
        if (user && liveDictation.isSupported) {
          try {
            liveDictation.start()
            liveDictationActiveRef.current = true
          } catch {
            liveDictationActiveRef.current = false
          }
        }
      } catch (error) {
        voiceStartInFlightRef.current = false
        safeConsole.error('Voice input failed to start:', error)
        setListening(false)
        voiceStreamRef.current?.getTracks().forEach((track) => track.stop())
        voiceStreamRef.current = null
        stopVoiceWaveform()
        toast.error(error instanceof Error ? error.message : 'Voice input failed to start')
      }
    }

    const stopListening = () => {
      const recorder = voiceRecorderRef.current

      if (!recorder || recorder.state === 'inactive') {
        setListening(false)
        return
      }

      if (voiceStopRequestedRef.current) return

      voiceStopRequestedRef.current = true

      try {
        recorder.requestData()
      } catch {
        // Some browsers throw if no data is ready yet.
      }

      window.setTimeout(() => {
        if (voiceRecorderRef.current !== recorder || recorder.state === 'inactive') {
          return
        }

        try {
          recorder.stop()
        } catch (error) {
          safeConsole.warn('Voice recorder stop failed:', error)
          setListening(false)
          voiceRecorderRef.current = null
          voiceStreamRef.current?.getTracks().forEach((track) => track.stop())
          voiceStreamRef.current = null
          stopVoiceWaveform()
          resetVoiceMemoDraft()
        }
      }, VOICE_RECORDER_FINAL_FLUSH_DELAY_MS)
    }

    const applyVoiceInputDevice = (deviceId: string | null) => {
      persistVoiceInputDevice(deviceId)
      setPendingVoiceInputDeviceId(deviceId)
      setVoiceDevicePopoverOpen(false)
      setSilentVoiceInputDeviceId(null)

      if (!listening) {
        return
      }

      const recorder = voiceRecorderRef.current
      voiceCancelRecordingRef.current = true
      void stopLiveDictation()

      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null
        recorder.onerror = null
        recorder.onstop = null

        try {
          recorder.stop()
        } catch (error) {
          safeConsole.warn('Voice recorder stop before mic switch failed:', error)
        }
      }

      voiceRecorderRef.current = null
      voiceStreamRef.current?.getTracks().forEach((track) => track.stop())
      voiceStreamRef.current = null
      stopVoiceWaveform()
      setListening(false)
      resetVoiceMemoDraft()

      window.setTimeout(() => {
        void startListening(deviceId)
      }, 0)
    }

    const restartListening = () => {
      if (!listening && !isTranscribingVoice) {
        void startListening()
      }
    }

    useEffect(() => {
      if (isPublic) return

      const focusVoiceMemo = () => {
        restartListening()
      }

      const drainPending = () => {
        void import('@/lib/discoverability/cta-actions').then(({ consumePendingFocusVoiceMemo }) => {
          if (consumePendingFocusVoiceMemo()) focusVoiceMemo()
        })
      }

      drainPending()
      window.addEventListener('losi:focus-voice-memo', focusVoiceMemo)
      return () => {
        window.removeEventListener('losi:focus-voice-memo', focusVoiceMemo)
      }
    }, [isPublic, listening, isTranscribingVoice])

    const handleAiIconClick = () => {
      setIsAssistantPickerOpen(true)
    }

    const sendComposerMessage = () => {
      if (isUploading || isTranscribingVoice) return

      if (listening) {
        voiceSendAfterStopRequestedRef.current = true
        stopListening()
        return
      }

      handleSendWithQuote()
    }

    // Hold-to-record: pressing the mic starts capture immediately (no waiting
    // for the click). Holding past VOICE_HOLD_TO_SEND_MIN_MS and releasing
    // stops, transcribes, and sends with the text included; a quick tap
    // latches recording on so the stop/send buttons take over hands-free.
    // The release is tracked on window because this button unmounts the
    // moment recording starts.
    const handleVoiceMicPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
      if (listening || isTranscribingVoice) return
      if (event.pointerType === 'mouse' && event.button !== 0) return

      const { pointerId } = event
      const startedAt = Date.now()
      voiceHoldGestureRef.current = { pointerId, startedAt }
      // The click that follows this press must not toggle the recorder again.
      voiceHoldClickGuardAtRef.current = startedAt
      void startListening()

      const detach = () => {
        window.removeEventListener('pointerup', handleRelease)
        window.removeEventListener('pointercancel', handleRelease)
        if (voiceHoldReleaseCleanupRef.current === detach) {
          voiceHoldReleaseCleanupRef.current = null
        }
      }
      const handleRelease = (releaseEvent: PointerEvent) => {
        if (releaseEvent.pointerId !== pointerId) return
        detach()
        if (voiceHoldGestureRef.current?.pointerId === pointerId) {
          voiceHoldGestureRef.current = null
        }

        if (releaseEvent.type === 'pointercancel') return
        // Quick tap: keep recording hands-free.
        if (Date.now() - startedAt < VOICE_HOLD_TO_SEND_MIN_MS) return
        // Mic still starting (e.g. permission prompt): latch once it is live
        // rather than sending an empty stub.
        if (!voiceRecorderRef.current) return

        voiceSendAfterStopRequestedRef.current = true
        stopListening()
      }

      voiceHoldReleaseCleanupRef.current?.()
      voiceHoldReleaseCleanupRef.current = detach
      window.addEventListener('pointerup', handleRelease)
      window.addEventListener('pointercancel', handleRelease)
    }

    const consumeVoiceMicClickIfHoldGesture = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (Date.now() - voiceHoldClickGuardAtRef.current > 1500) return
      voiceHoldClickGuardAtRef.current = 0
      event.preventDefault()
    }

    const composerIconButtonClassName =
      'm-1 inline-flex size-10 touch-manipulation items-center justify-center rounded-full p-0 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:size-9'
    const stopComposerButtonClassName =
      'border border-border/70 bg-background/80 text-foreground shadow-sm hover:bg-muted hover:text-foreground dark:border-white/10 dark:bg-neutral-900/85 dark:text-neutral-100 dark:hover:bg-neutral-800'
    const sendComposerButtonClassName =
      'm-1 inline-flex size-10 touch-manipulation items-center justify-center bg-primary p-0.5 text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:size-9'

    const renderSendButton = (options?: {
      label?: string
      title?: string
      rounded?: 'full' | 'lg'
      disabled?: boolean
    }) => {
      const disabled = options?.disabled ?? (isUploading || isTranscribingVoice)

      return (
        <button
          type="button"
          aria-label={options?.label || 'Send message'}
          title={options?.title}
          disabled={disabled}
          onClick={sendComposerMessage}
          className={cn(
            sendComposerButtonClassName,
            options?.rounded === 'full' ? 'rounded-full' : 'rounded-lg'
          )}
        >
          <IconArrowUp aria-hidden="true" className="block" stroke={2} size={30} />
        </button>
      )
    }

    const silentVoiceInputDevice =
      voiceInputDevices.find((device) => device.deviceId === silentVoiceInputDeviceId) ||
      voiceInputDevices.find((device) => device.deviceId === selectedVoiceInputDeviceId) ||
      null
    const alternativeVoiceInputDevices = voiceInputDevices.filter((device) => {
      if (device.deviceId === silentVoiceInputDeviceId || device.deviceId === selectedVoiceInputDeviceId) {
        return false
      }

      return !(device.isDefault && Boolean(silentVoiceInputDeviceId))
    })
    const pendingVoiceInputDevice =
      alternativeVoiceInputDevices.find((device) => device.deviceId === pendingVoiceInputDeviceId) ||
      voiceInputDevices.find((device) => device.deviceId === pendingVoiceInputDeviceId) ||
      alternativeVoiceInputDevices[0] ||
      null
    const shouldShowVoiceInputControls = voiceInputSupported || isPublic
    const shouldShowVoiceMemoPreview = listening || isTranscribingVoice || Boolean(voiceMemo)
    const voiceMemoPreviewDuration = voiceMemo?.durationSeconds ?? voiceRecordingDurationSeconds

    // const handleToggleCodeEditor = async () => {
    //   if (!profile) return

    //   const updatedProfile = await updateProfile(profile.id, {
    //     ...profile,
    //     experimental_code_editor: !profile.experimental_code_editor
    //   })

    //   setProfile(updatedProfile)
    // }

    return (
      <motion.div
        id="chat-input"
        layout={composerLayoutId ? 'position' : false}
        layoutId={composerLayoutId}
        transition={{ layout: { type: 'spring', stiffness: 420, damping: 44, mass: 0.85 } }}
        className="relative"
      >
        <ChatFilesDisplay chatId={chatId} />
        {/* Active Note display anchored to chat input */}
        {activeNote && (
          <div className="mx-2 my-1 flex items-start gap-2 rounded-lg border bg-background/70 px-3 py-2 text-xs text-foreground/90 backdrop-blur">
            <div className="truncate font-semibold">Active Note: {activeNote.title}</div>
            <div className="line-clamp-2 flex-1 text-muted-foreground">{activeNote.body}</div>
          </div>
        )}
        <ChatCommandInput
          chatId={chatId}
          isMentionPickerEnabled={!isStructuredMentionMode}
          mentionOptions={unifiedMentionOptions}
          onSelectMention={handleSelectMentionOption}
          onUseAssistantAsActive={assistant => {
            void handleSelectAssistant(assistant)
            setUserInput(prev => prev.replace(/@\w*$/, ''))
          }}
        />
        <ChatSelectedHtmlElements />
        {spaceItemContext && (
          <SpaceItemContextChip
            item={spaceItemContext}
            onOpen={(item) => {
              const baseSpacePath = `/spaces/${item.spaceId}`

              if (pathname === baseSpacePath || pathname?.startsWith(`${baseSpacePath}/`)) {
                dispatchSidekickOpenSpaceItem(item)
                return
              }

              handleSplitNavigateToHref(getSidekickSpaceItemHref(item))
            }}
            onRemove={() => handlerOverride?.clearSpaceItemContext?.()}
          />
        )}
        {quotedText && (
          <QuotedText
            text={quotedText.text}
            messageId={quotedText.messageId}
            onClose={() => setQuotedText(null)}
            onScrollToSource={
              chatId
                ? (quotedMessageId, quotedTextContent) => {
                    try {
                      sessionStorage.setItem(
                        'highlight-quote',
                        JSON.stringify({
                          messageId: quotedMessageId,
                          text: quotedTextContent || ''
                        })
                      )
                    } catch {
                      // ignore storage errors
                    }
                    const targetUrl = `/chat/${chatId}?highlight=${quotedMessageId}`
                    const isSameChat = pathname === `/chat/${chatId}`
                    if (isSameChat) {
                      router.replace(targetUrl)
                    } else {
                      handleSplitNavigateToHref(targetUrl)
                    }
                  }
                : undefined
            }
          />
        )}
        {selectedTextContext && (
          <SelectedText text={selectedTextContext} onClose={() => setSelectedTextContext(null)} />
        )}
        {queuedSidecarTurns.length > 0 &&
          onRemoveQueuedTurn &&
          (onUpdateQueuedTurn && onSendQueuedTurnNow ? (
            <>
              {editingQueuedId && (
                <div className="mb-2 flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-100/95 p-3 dark:border-neutral-700 dark:bg-neutral-800/95">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground/90">
                      Editing queued message
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 shrink-0 text-xs"
                      onClick={() => {
                        setEditingQueuedId(null)
                        setEditingQueuedDraft('')
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <textarea
                    value={editingQueuedDraft}
                    onChange={(e) => setEditingQueuedDraft(e.target.value)}
                    placeholder="Message text"
                    rows={2}
                    className="min-h-0 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-base leading-normal focus:outline-none focus:ring-2 focus:ring-ring md:text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 text-xs"
                      disabled={!editingQueuedDraft.trim()}
                      onClick={() => {
                        if (editingQueuedId && editingQueuedDraft.trim() && onUpdateQueuedTurn) {
                          onUpdateQueuedTurn(editingQueuedId, editingQueuedDraft.trim())
                          setEditingQueuedId(null)
                          setEditingQueuedDraft('')
                        }
                      }}
                    >
                      Update
                    </Button>
                  </div>
                </div>
              )}
              <QueuedMessagesBlock
                turns={queuedSidecarTurns}
                onRemove={onRemoveQueuedTurn}
                onEdit={(id, message) => {
                  setEditingQueuedId(id)
                  setEditingQueuedDraft(message)
                }}
                onSendNow={onSendQueuedTurnNow}
              />
            </>
          ) : (
            <QueuedMessagesBlock
              turns={queuedSidecarTurns}
              onRemove={onRemoveQueuedTurn}
              onEdit={() => {}}
              onSendNow={() => {}}
            />
          ))}
        <ChatTypingIndicator typers={remoteTypers} />
        {voiceDevicePopoverOpen && (
          <div
            className="absolute bottom-full right-2 z-[2147482001] mb-3 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-border/70 bg-popover p-3 text-popover-foreground shadow-2xl"
            role="dialog"
            aria-label="Switch microphone"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold">No sound detected</div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  No sound is coming from {getVoiceInputDeviceLabel(silentVoiceInputDevice)}.
                  {alternativeVoiceInputDevices.length > 0
                    ? ' Switch to another microphone and try again.'
                    : ' Choose a different input in your system audio settings.'}
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss microphone switcher"
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setVoiceDevicePopoverOpen(false)}
              >
                <IconX aria-hidden="true" className="size-4" />
              </button>
            </div>

            {alternativeVoiceInputDevices.length === 1 ? (
              <Button
                type="button"
                className="h-9 w-full rounded-full text-xs font-semibold"
                onClick={() => applyVoiceInputDevice(alternativeVoiceInputDevices[0].deviceId)}
              >
                Switch to {alternativeVoiceInputDevices[0].label}
              </Button>
            ) : alternativeVoiceInputDevices.length > 1 ? (
              <div className="space-y-3">
                <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
                  {alternativeVoiceInputDevices.map((device) => {
                    const selected = pendingVoiceInputDevice?.deviceId === device.deviceId

                    return (
                      <button
                        key={device.deviceId}
                        type="button"
                        className={cn(
                          'flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                          selected
                            ? 'border-primary/50 bg-primary/10 text-foreground'
                            : 'border-border/60 bg-background/70 text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                        )}
                        onClick={() => setPendingVoiceInputDeviceId(device.deviceId)}
                      >
                        <span className="min-w-0 truncate">{device.label}</span>
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-full border',
                            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
                          )}
                        >
                          {selected ? <IconCheck aria-hidden="true" className="size-3" /> : null}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 rounded-full px-3 text-xs"
                    disabled={!pendingVoiceInputDevice}
                    onClick={() => applyVoiceInputDevice(pendingVoiceInputDevice?.deviceId ?? null)}
                  >
                    <IconCheck aria-hidden="true" className="mr-1 size-3.5" />
                    Confirm switch
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="h-9 w-full rounded-full text-xs"
                onClick={() => {
                  setVoiceDevicePopoverOpen(false)
                  void refreshVoiceInputDevices()
                }}
              >
                Check microphones again
              </Button>
            )}
          </div>
        )}
        {showChatRealtime ? (
        <RealtimeComposerBar
          chatId={
            selectedChat?.id ||
            selectedChatForInput?.id ||
            chatId ||
            null
          }
          enabledToolIds={(selectedTools || [])
            .map(tool => (tool && typeof tool.id === 'string' ? tool.id : ''))
            .filter(Boolean)}
          surface={handlerOverride ? 'sidekick' : 'chat'}
          isActive={Boolean(advancedVoiceMode)}
          hasThreadHistory={Boolean(
            (effectiveChatMessages?.length ?? 0) > 0
          )}
          onActiveChange={active => {
            // onConnected must NEVER create a chat — that raced a second
            // empty chat when draft /chat cleared selectedChat (BUG1).
            if (active) {
              handleConfirmAdvancedVoiceMode(
                selectedChat?.id || selectedChatForInput?.id || chatId || null
              )
            } else {
              handleEndAdvancedVoiceMode()
            }
          }}
          onTranscript={(text, role, meta) => onTranscriptUpdate(text, role, meta)}
          onRealtimeEvent={event => onRealtimeInferenceEvent(event)}
          onNavigate={href => {
            handleSplitNavigateToHref(href)
          }}
          onFilesSelect={async files => {
            if (files.length === 0) return
            if (typeof handleSelectDeviceFiles === 'function') {
              await handleSelectDeviceFiles(files)
              return
            }
            for (const file of files) {
              handleSelectDeviceFile(file)
            }
          }}
        />
        ) : null}
        <FloatingSurface
          variant="input"
          radius="pill"
          className={cn(
            'transparent-input flex w-full flex-col justify-end',
            !isStructuredMentionMode && 'overflow-hidden'
          )}
        >
          {showAssistant &&
            !handlerOverride?.selectedAssistant &&
            (() => {
              const a = selectedAssistant
              if (!a) return null
              return (
                <div className="flex items-center justify-between space-x-2 border-b border-input bg-accent p-2 pl-4 pr-3">
                  <div className={'flex items-center space-x-2'}>
                    <AssistantIcon assistant={a} size={24} excludeSpecificLogos={false} />
                    <div className="text-sm font-semibold">Talking to {a.name}</div>
                  </div>
                  <Button
                    aria-label="Clear selected assistant"
                    className="size-8 rounded-full text-muted-foreground hover:text-foreground"
                    onClick={() => setSelectedAssistant(null)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <IconX aria-hidden="true" className="size-4" stroke={1.5} />
                  </Button>
                </div>
              )
            })()}

          {shouldShowVoiceMemoPreview ? (
            <VoiceMemoComposerPreview
              audioUrl={voiceMemo?.audioUrl ?? null}
              contentType={voiceMemo?.contentType ?? null}
              durationSeconds={voiceMemoPreviewDuration}
              isRecording={listening}
              isTranscribing={isTranscribingVoice}
              liveTranscript={listening ? liveDictation.interimText : undefined}
              onDiscard={discardVoiceMemoDraft}
              samples={voiceWaveformSamples}
              transcript={voiceMemo?.transcript ?? transcript}
              onTranscriptChange={updateVoiceMemoTranscript}
            />
          ) : null}

          <div className="flex items-start justify-between p-2">
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col gap-1">
              {/* Animated placeholder overlay: only when empty so no overlap with typed text */}
              {!userInput && (
                <div
                  className={cn(
                    'pointer-events-none absolute left-0 top-0 z-10 flex size-full items-start overflow-hidden px-2 pb-4',
                    isStructuredMentionMode ? 'pt-3.5' : 'pt-4'
                  )}
                >
                  <span
                    className="text-md flex text-muted-foreground"
                    style={{ willChange: 'contents' }}
                    aria-hidden="true"
                  >
                    {hasConversationStarted ? (
                      // Show full placeholder immediately when conversation has started
                      <span>{placeholderText}</span>
                    ) : (
                      // Animated typewriter effect when no conversation.
                      // Decorative only (aria-hidden) — per-letter spans must not
                      // be announced by screen readers.
                      <AnimatePresence mode="popLayout">
                        {placeholderText.split('').map((char, index) => {
                          const isVisible = index < displayedChars
                          if (!isVisible) return null
                          return (
                            <motion.span
                              key={`${placeholderText}-${index}`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{
                                duration: 0.15,
                                ease: 'easeOut'
                              }}
                              style={{ willChange: 'opacity' }}
                            >
                              {char === ' ' ? '\u00A0' : char}
                            </motion.span>
                          )
                        })}
                      </AnimatePresence>
                    )}
                  </span>
                </div>
              )}
              {/* Structured mentions: use react-mentions for proper chips */}
              {isStructuredMentionMode ? (
                <CrmMentionsInput
                  value={userInput}
                  onChange={(v) => {
                    if (!isInputReady) return
                    handleInputChangeWithAutocomplete(v)
                    syncComposerTypingState(v)
                  }}
                  onBlur={stopComposerTyping}
                  onKeyDown={(e) => {
                    if (!isInputReady) return
                    handleKeyDown(e as React.KeyboardEvent<HTMLTextAreaElement>)
                  }}
                  onPaste={(event) => {
                    if (!isInputReady) return
                    handlePaste(event)
                  }}
                  placeholder={''}
                  workspaceId={legacyCrmMentionWorkspaceId}
                  mentionConfig={activeStructuredMentionConfig}
                  onAdd={(id) => {
                    const [type, assistantId] = id.split(':')
                    if (type !== 'assistant' || !assistantId) return

                    const assistant = assistants.find((candidate) => candidate.id === assistantId)
                    if (assistant) {
                      handleSelectAssistantMention(assistant)
                    }
                  }}
                  inputRef={inputRef}
                  disabled={!isInputReady}
                  aria-label="Chat input"
                  className={cn('w-full', STRUCTURED_COMPOSER_DRAFT_SCROLL_CLASS_NAME)}
                />
              ) : (
                <TextareaAutosize
                  textareaRef={inputRef}
                  className={cn(
                    'z-100 relative w-full resize-none rounded-md border-none bg-transparent p-4 px-2 text-base leading-normal ring-offset-background focus-visible:outline-none focus-visible:ring-ring',
                    COMPOSER_DRAFT_SCROLL_CLASS_NAME,
                    !isInputReady && 'cursor-not-allowed opacity-50'
                  )}
                  placeholder=""
                  onValueChange={(value) => {
                    if (!isInputReady) return
                    handleInputChangeWithAutocomplete(value)
                    syncComposerTypingState(value)
                  }}
                  value={userInput}
                  maxRows={8}
                  onKeyDown={(event) => {
                    if (!isInputReady) return
                    if (event.key === 'Enter') {
                      safeConsole.log('Enter key pressed', {
                        isTyping,
                        isUploading,
                        userInputLength: userInput.trim().length,
                        isGenerating: effectiveIsGenerating
                      })
                    }
                    handleKeyDown(event as React.KeyboardEvent<HTMLTextAreaElement>)
                  }}
                  onPaste={(event) => {
                    if (!isInputReady) return
                    handlePaste(event)
                  }}
                  onBlur={stopComposerTyping}
                  onCompositionStart={() => {
                    setIsTyping(true)
                    void setLocalTyping(true)
                  }}
                  onCompositionEnd={() => {
                    setTimeout(() => {
                      const value = inputRef.current?.value ?? ''
                      syncComposerTypingState(value)
                    }, 10)
                  }}
                />
              )}
              {autocompleteSuggestion && isAssistantAutocompleteEnabled && (
                <div
                  className="pointer-events-none absolute left-0 top-0 size-full overflow-hidden p-4 px-2 text-base leading-normal text-muted-foreground"
                  style={{ zIndex: 0 }}
                >
                  {userInput + autocompleteSuggestion}
                </div>
              )}
              {/* Extension name highlighting overlay */}
              {detectedExtension && (
                <div
                  className="pointer-events-none absolute left-0 top-0 flex size-full items-start p-4 px-2"
                  style={{ zIndex: 1 }}
                >
                  <span className="mr-1 inline-block rounded border border-primary/25 bg-primary/10 px-1 font-bold text-primary">
                    {detectedExtension.matchedKeyword}
                  </span>
                  {detectedExtension.remainingMessage && (
                    <span className="opacity-60">{detectedExtension.remainingMessage}</span>
                  )}
                </div>
              )}
              <AnimatePresence>
                {selectedAssistantAnimation && (
                  <motion.div
                    key={selectedAssistantAnimation.id}
                    initial={{ opacity: 0, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(10px)' }}
                    transition={{ duration: 0.5 }}
                    className="absolute right-2 top-0 z-10"
                  >
                    <AssistantIcon
                      assistant={selectedAssistantAnimation}
                      size={24}
                      excludeSpecificLogos={false}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              <AttachmentDropdown
                filesToAccept={filesToAccept}
                onFileSelect={handleSelectDeviceFile}
                onFilesSelect={handleSelectDeviceFiles}
                isUploading={isUploading}
                isAuthenticated={!!user}
                isPublic={isPublic}
                chatId={chatId}
                imagesSupported={imagesSupportedByActiveModel}
              />
              {uploadStatusLabel && (
                <div
                  className="border-border/50 bg-background/75 text-muted-foreground flex min-w-0 items-center gap-2 rounded-full border px-2.5 py-1 text-xs"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  aria-label={uploadAccessibleLabel || undefined}
                >
                  <span
                    className="relative size-5 shrink-0 rounded-full"
                    role="progressbar"
                    aria-label={uploadAccessibleLabel || undefined}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={uploadProgressPercent}
                    aria-valuetext={uploadProgressValueText}
                    style={{
                      background: `conic-gradient(hsl(var(--primary)) ${uploadProgressDegrees}deg, hsl(var(--muted)) 0deg)`
                    }}
                  >
                    <span className="bg-background absolute inset-[3px] rounded-full" />
                  </span>
                  {uploadStatusFileName && (
                    <span className="sr-only">File: {uploadStatusFileName}</span>
                  )}
                  <span className="min-w-0 max-w-48 truncate">{uploadStatusLabel}</span>
                  <span
                    className="text-muted-foreground/70 shrink-0 tabular-nums"
                    aria-hidden="true"
                  >
                    {uploadProgressPercent}%
                  </span>
                  {uploadProgressSize && (
                    <span className="text-muted-foreground/60 hidden shrink-0 sm:inline">
                      {uploadProgressSize}
                    </span>
                  )}
                </div>
              )}
              {!hideToolControlsForOverride && (!isPublic || showWebBrowseToggle) && (
                <div className="border-border/50 mx-1 hidden h-6 border-l sm:block" />
              )}
              {!isPublic && !hideToolControlsForOverride && <ToolDropdown />}
              {showWebBrowseToggle && <WebBrowseToggle />}
              {/*
            <ImageGenToggle />
            <VideoGenToggle />
            <MusicGenToggle />
            */}
              {!hideToolControlsForOverride && activeChatSettings?.model === 'losi-omega' && (
                <>
                  <div className="border-border/50 mx-1 hidden h-6 border-l sm:block" />
                  <ThinkSelector />
                </>
              )}
              {!hideToolControlsForOverride &&
                (isPublic || showReasoningButton) &&
                activeChatSettings?.model === 'losi-mini' && (
                  <>
                    <div className="border-border/50 mx-1 hidden h-6 border-l sm:block" />
                    <PublicReasoningToggle />
                  </>
                )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-0">
              <div className="flex flex-nowrap overflow-hidden">
                {shouldShowVoiceInputControls &&
                !effectiveIsGenerating &&
                !effectiveIsPersisting &&
                !sidecarRunActive ? (
                  listening ? (
                    <div className="flex items-center gap-1">
                      <SpeechInput
                        type="button"
                        isListening={listening}
                        onStop={stopListening}
                        size="icon"
                        className={cn(
                          composerIconButtonClassName,
                          'border border-red-500/60 bg-transparent text-red-500 hover:bg-red-500/10 hover:text-red-600'
                        )}
                        aria-label="Stop voice input"
                      >
                        <IconPlayerStopFilled
                          aria-hidden="true"
                          stroke={2}
                          className="animate-pulse"
                          size={22}
                        />
                      </SpeechInput>
                      {renderSendButton({
                        label: 'Send voice message',
                        title: 'Send voice message',
                        rounded: 'full'
                      })}
                    </div>
                  ) : isTranscribingVoice ? (
                    <div className={'gap-lg flex flex-col'}>
                      <SpeechInput
                        type="button"
                        isProcessing={isTranscribingVoice}
                        disabled
                        size="icon"
                        className={composerIconButtonClassName}
                        aria-label="Transcribing voice input"
                      >
                        <Loader2 aria-hidden="true" className="size-5 animate-spin" />
                      </SpeechInput>
                    </div>
                  ) : canSendComposerMessage ? (
                    <div className="flex items-center gap-1">
                      {!advancedVoiceMode && showChatRealtime ? (
                        <button
                          type="button"
                          className={composerIconButtonClassName}
                          aria-label="Start realtime"
                          title="Start realtime — opens a new chat if you need one"
                          onClick={() => void handleStartAdvancedVoiceMode()}
                        >
                          <IconWaveSine aria-hidden="true" stroke={2} size={22} />
                        </button>
                      ) : null}
                      {renderSendButton({ rounded: 'full' })}
                    </div>
                  ) : (
                    <div className={'gap-lg flex items-center'}>
                      {!advancedVoiceMode && showChatRealtime ? (
                        <button
                          type="button"
                          className={composerIconButtonClassName}
                          aria-label="Start realtime"
                          title="Start realtime — opens a new chat if you need one"
                          onClick={() => void handleStartAdvancedVoiceMode()}
                        >
                          <IconWaveSine aria-hidden="true" stroke={2} size={22} />
                        </button>
                      ) : null}
                      <SpeechInput
                        type="button"
                        isListening={false}
                        onStart={restartListening}
                        onClick={consumeVoiceMicClickIfHoldGesture}
                        onContextMenu={(event) => event.preventDefault()}
                        onPointerDown={handleVoiceMicPointerDown}
                        size="icon"
                        className={cn(
                          composerIconButtonClassName,
                          'touch-none select-none [-webkit-touch-callout:none]'
                        )}
                        aria-label="Start voice input"
                        title="Hold to record, release to send · tap to record hands-free"
                      >
                        <IconMicrophone aria-hidden="true" stroke={2} size={24} />
                      </SpeechInput>
                    </div>
                  )
                ) : (effectiveIsGenerating || effectiveIsPersisting || sidecarRunActive) &&
                  canQueueFollowUp &&
                  canSendComposerMessage ? (
                  renderSendButton({
                    label: t('chat.queue_follow_up', 'Queue follow-up'),
                    title: t('chat.queue_follow_up', 'Queue follow-up')
                  })
                ) : effectiveIsGenerating ? (
                  <button
                    aria-label="Stop generating"
                    className={cn(
                      composerIconButtonClassName,
                      stopComposerButtonClassName,
                      'animate-pulse'
                    )}
                    onClick={() => handleStopMessage()}
                    title="Stop generating"
                    type="button"
                  >
                    <IconPlayerStopFilled aria-hidden="true" size={24} stroke={2} />
                  </button>
                ) : effectiveIsPersisting && canSendComposerMessage ? (
                  renderSendButton({
                    label: t('chat.saving_previous_message', 'Saving previous message...'),
                    title: t('chat.saving_previous_message', 'Saving previous message...'),
                    disabled: true
                  })
                ) : sidecarRunActive && canSendComposerMessage ? (
                  renderSendButton({
                    label: t(
                      'chat.current_response_running',
                      'Current response is still running...'
                    ),
                    title: t(
                      'chat.current_response_running',
                      'Current response is still running...'
                    ),
                    disabled: true
                  })
                ) : canSendComposerMessage ? (
                  renderSendButton()
                ) : (
                  renderSendButton({
                    label: t('chat.type_message_to_send', 'Type a message to send'),
                    title: t('chat.type_message_to_send', 'Type a message to send'),
                    disabled: true,
                    rounded: 'full'
                  })
                )}
              </div>
            </div>
          </div>
        </FloatingSurface>

        {/* Floating AI note actions toolbar for general text selections */}
        {/* {selectionInputVisible && selectedText && (
        <div
          className="fixed z-50 rounded-lg border bg-background/95 backdrop-blur px-2 py-1 shadow-lg flex gap-1"
          style={{
            left: selectionPosition.x,
            top: selectionPosition.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          {noteActions.map(action => (
            <button
              key={action.id}
              className="text-xs px-2 py-1 rounded hover:bg-accent text-foreground"
              onClick={() => {
                const prompt = action.buildPrompt(selectedText)
                if (action.id === 'ask_losi') {
                  document.dispatchEvent(new CustomEvent('populateInput', { detail: { prompt } }))
                } else {
                  document.dispatchEvent(new CustomEvent('sendPrompt', { detail: { prompt } }))
                }
              }}
            >
              <span className="inline-flex items-center gap-1">
                <IconSparkle2 size={14} />
                {action.label}
              </span>
            </button>
          ))}
        </div>
      )} */}
      </motion.div>
    )
  },
  areInputPropsEqual
)

// Add displayName to fix linter error
ChatInputInternal.displayName = 'ChatInputInternal'

// Export the memoized component
export const ChatInput = ChatInputInternal
